import { useEffect, useMemo, useState, useCallback, useRef, Fragment } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, GripVertical, Clock, X, Trash2, ChevronsUpDown, Check, Plus, Ban, Lock, AlertTriangle, CalendarPlus, Share2, RotateCw, Sparkles, CheckSquare, UserRound, ListChecks, Circle, CheckCircle2, MousePointerClick, Info as InfoIcon, BellOff, StickyNote, Mic, Square, Loader2, Activity } from "lucide-react";
import {
  startOfWeek, addDays, addWeeks, format, isSameDay, parseISO,
  startOfMonth, endOfMonth, addMonths, isSameMonth,
} from "date-fns";
import { nl } from "date-fns/locale";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Command, CommandInput, CommandList, CommandItem, CommandEmpty, CommandGroup,
} from "../../components/ui/command";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { TODO_COLORS, onColor } from "../../lib/todoColors";

const CORE_START = 9;   // normale werkband-start
const CORE_END = 18;    // normale werkband-eind
const STEP = 15;
const SLOT_H = 24;

const pad = (n) => String(n).padStart(2, "0");
const toLocalIso = (d) => `${format(d, "yyyy-MM-dd")}T${format(d, "HH:mm:ss")}`;

function computeRestWarnings(treatment, start, appts) {
  const ns = start.getHours() * 60 + start.getMinutes();
  const ne = ns + (treatment?.duration_min || 0);
  const restNew = treatment?.rest_min || 0;
  const warns = [];
  appts.filter((a) => a.status !== "cancelled" && isSameDay(parseISO(a.start), start)).forEach((a) => {
    const as = (() => { const d = parseISO(a.start); return d.getHours() * 60 + d.getMinutes(); })();
    const ae = as + (a.duration_min || 0);
    const restA = a.rest_min || 0;
    if (ns < ae && as < ne) {
      warns.push(`Overlapt met ${a.customer_name} (${a.treatment_name}).`);
    } else if (ns >= ae) {
      const gap = ns - ae;
      if (gap < restA) warns.push(`Te weinig rust na ${a.treatment_name} (${a.customer_name}): ${gap} min i.p.v. ${restA} min.`);
    } else if (ne <= as) {
      const gap = as - ne;
      if (gap < restNew) warns.push(`Te weinig rust vóór ${a.customer_name}: ${gap} min i.p.v. ${restNew} min.`);
    }
  });
  return warns;
}

export default function CalendarPage() {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [viewFilter, setViewFilter] = useState(() => {
    try { return localStorage.getItem("agendaViewFilter") || "all"; } catch { return "all"; }
  }); // all | treatments | private
  const [treatments, setTreatments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [appts, setAppts] = useState([]);
  const [avail, setAvail] = useState({ hours: [], overrides: [] });
  const [drag, setDrag] = useState(null);
  const [modal, setModal] = useState(null); // {treatment, start}
  const [customerId, setCustomerId] = useState("");
  const [notes, setNotes] = useState("");
  const [detail, setDetail] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [blockModal, setBlockModal] = useState(null); // {date, start, end, note}
  const [todos, setTodos] = useState([]);
  const [privateModal, setPrivateModal] = useState(null); // {date, start, end, title, color}
  const [todoDetail, setTodoDetail] = useState(null); // scheduled todo clicked in agenda
  const [warnings, setWarnings] = useState([]);
  const [blockWarn, setBlockWarn] = useState([]);
  const [privWarn, setPrivWarn] = useState([]);
  const [slotAction, setSlotAction] = useState(null); // {day, slotIndex, endIndex}
  const [assistOpen, setAssistOpen] = useState(false);
  const [assistHelp, setAssistHelp] = useState(false);
  const [assistCustomerId, setAssistCustomerId] = useState("");
  const [assist, setAssist] = useState({ type: "treatment", treatment_id: "", duration_min: 30, load: 2, when: "this_week", within_days: 7, daypart: "any", title: "" });
  const [assistResults, setAssistResults] = useState(null);
  const [assistLoading, setAssistLoading] = useState(false);
  const runAssist = async () => {
    setAssistLoading(true);
    try {
      const body = { type: assist.type, when: assist.when, within_days: Number(assist.within_days) || 7, daypart: assist.daypart, count: 6 };
      if (assist.type === "treatment") body.treatment_id = assist.treatment_id;
      else body.duration_min = Number(assist.duration_min) || 30;
      if (Number(assist.load)) body.load = Number(assist.load);
      const r = await api.post("/admin/planner/suggest", body);
      setAssistResults(r.data);
    } catch (e) { toast.error("Kon geen voorstellen ophalen."); }
    setAssistLoading(false);
  };
  const applySuggestion = (s) => {
    setAssistOpen(false);
    if (assist.type === "treatment") {
      setApptTreatmentId(assist.treatment_id); setCustomerId(assistCustomerId || ""); setNotes(""); setWarnings([]);
      setModal({ start: new Date(s.start_iso), closedDay: false });
    } else {
      setBlockModal({ date: s.date, start: s.start, end: s.end, note: assist.title || "", title: assist.title || "", kind: assist.type === "private" ? "private" : "block" });
    }
  };
  const [selecting, setSelecting] = useState(false);
  const [selAnchor, setSelAnchor] = useState(null); // {di, i}
  const [selCur, setSelCur] = useState(null);
  const [selDay, setSelDay] = useState(null);
  const [legendOpen, setLegendOpen] = useState(false);
  const justSelectedRef = useRef(false);
  const gridRef = useRef(null);
  const lpTimer = useRef(null);
  const touchStartRef = useRef(null);
  const touchSelectingRef = useRef(false);
  const lastTapRef = useRef({ t: 0, di: null, i: null });
  const isPlanner = typeof window !== "undefined" && window.location.pathname.startsWith("/planner");
  const [selectMode, setSelectMode] = useState(() => { try { return localStorage.getItem("agendaSelectMode") === "1"; } catch { return false; } });
  const [rescheduleDrag, setRescheduleDrag] = useState(null); // {appt, start}
  const [armedRes, setArmedRes] = useState(null); // {reservationId, customerId, treatmentId, name, notes}
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const rid = searchParams.get("reservation");
    if (!rid) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/reservations");
        const r = data.find((x) => x.id === rid);
        if (!r) { toast.error("Aanvraag niet gevonden."); return; }
        let cid = r.customer_id || r.existing_customer_id;
        if (!cid) {
          const res = await api.post(`/admin/reservations/${rid}/to-customer`);
          cid = res.data.customer.id;
        }
        setArmedRes({ reservationId: rid, customerId: cid, treatmentId: r.treatment_id || "", name: r.name, notes: r.message || "" });
        loadCustomers();
        toast.info(`Kies een tijdvak voor de afspraak van ${r.name}.`, { duration: 6000 });
      } catch (e) { toast.error("Kon aanvraag niet laden."); }
      finally {
        searchParams.delete("reservation");
        setSearchParams(searchParams, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [armed, setArmed] = useState(null); // treatment id armed for tap-to-place (mobile-friendly)
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches);
  const [mobileIdx, setMobileIdx] = useState(0);
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("agendaView") || (window.matchMedia("(max-width: 1023px)").matches ? "day" : "week"); } catch { return "week"; }
  }); // day | week | month
  const [monthAnchor, setMonthAnchor] = useState(() => startOfMonth(new Date()));
  const [monthData, setMonthData] = useState({ appts: [], blocks: [] });
  const [exceptionDay, setExceptionDay] = useState(null);
  const [dayDetail, setDayDetail] = useState(null);
  const [dayMeta, setDayMeta] = useState({});
  const [dayEdit, setDayEdit] = useState(null);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const on = () => setIsMobile(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const [apptTreatmentId, setApptTreatmentId] = useState("");
  const [dragOver, setDragOver] = useState(null); // {di, i}
  const [allDayOver, setAllDayOver] = useState(null); // date string of hovered all-day zone

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const [showWeekend, setShowWeekend] = useState(true);
  const weekDays = useMemo(() => (showWeekend ? days : days.slice(0, 5)), [days, showWeekend]);
  const [ext, setExt] = useState(false);
  const [earlyHour, setEarlyHour] = useState(7);
  const [lateHour, setLateHour] = useState(21);
  const [markExt, setMarkExt] = useState(true);
  const [extColor, setExtColor] = useState("#ECEAF6");
  const [dailyMax, setDailyMax] = useState(8);
  const [perDayMax, setPerDayMax] = useState([0, 0, 0, 0, 0, 0, 0]);
  const loadFromDur = (m) => (m <= 20 ? 1 : m <= 40 ? 2 : m <= 60 ? 3 : m <= 90 ? 4 : 5);
  const dayLoadFor = useCallback((date) => {
    const ds = format(date, "yyyy-MM-dd");
    let load = 0;
    appts.forEach((a) => { if (["confirmed", "reschedule_pending", "proposal_pending"].includes(a.status) && (a.start || "").slice(0, 10) === ds) load += loadFromDur(a.duration_min || 30); });
    blocks.forEach((b) => {
      if (b.date !== ds) return;
      if (b.load) { load += b.load; return; }
      if (b.kind === "private" || b.kind === "todo" || b.todo_id) {
        try { const [h1, m1] = b.start.split(":").map(Number); const [h2, m2] = b.end.split(":").map(Number); load += loadFromDur((h2 * 60 + m2) - (h1 * 60 + m1)); } catch { /* ignore */ }
      }
    });
    todos.forEach((t) => { if (!t.done && !t.scheduled_start && t.due_date === ds) load += ((t.load || 0) || loadFromDur(t.duration_min || 30)); });
    const dm = dayMeta[ds];
    if (dm && dm.manual_load) load += dm.manual_load;
    return load;
  }, [appts, blocks, todos, dayMeta]);
  const maxForDay = (date) => (perDayMax[(date.getDay() + 6) % 7] || 0) || dailyMax;
  const LoadBar = ({ date }) => {
    const l = dayLoadFor(date);
    const mx = maxForDay(date);
    const pct = Math.min(100, Math.round((l / mx) * 100));
    const over = l > mx;
    const col = over ? "#C2607A" : l >= mx * 0.75 ? "#E0BE73" : "#B78A6E";
    return (
      <div className="mt-1 px-2" data-testid={`day-load-${format(date, "yyyy-MM-dd")}`} title={over ? `Overvol — belasting ${l}/${mx} (boven maximum)` : `Belasting ${l}/${mx}`}>
        <div className="flex items-center gap-1">
          {over && <AlertTriangle className="w-3 h-3 shrink-0 text-[#C2607A]" data-testid={`day-overload-${format(date, "yyyy-MM-dd")}`} />}
          <div className="flex-1 h-1 rounded-full bg-[#EFEAE2] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: col }} /></div>
          <span className={`text-[9px] tabular-nums ${over ? "text-[#C2607A] font-semibold" : "text-ink-muted"}`}>{l}/{mx}</span>
        </div>
      </div>
    );
  };
  const toMin = (t) => { const [H, M] = (t || "0:0").split(":").map(Number); return H * 60 + M; };
  const ovMap = useMemo(() => Object.fromEntries((avail.overrides || []).map((o) => [o.date, o])), [avail.overrides]);
  const hoursMap = useMemo(() => { const m = {}; (avail.hours || []).forEach((h) => { m[h.day] = h; }); return m; }, [avail.hours]);
  const dayAvail = useCallback((date) => {
    const key = format(date, "yyyy-MM-dd");
    const ov = ovMap[key];
    if (ov) return ov.mode === "closed" ? { open: false } : { open: true, start: ov.start, end: ov.end };
    const md = (date.getDay() + 6) % 7;
    const h = hoursMap[md];
    if (!h || !h.enabled) return { open: false };
    return { open: true, start: h.start, end: h.end };
  }, [ovMap, hoursMap]);
  // Zichtbare tijdband volgt de werkelijke openingstijden (+ afspraken/blokkades in beeld + evt. verbrede uren)
  const gridRange = useMemo(() => {
    let sMin = CORE_START * 60, eMax = CORE_END * 60;
    days.forEach((d) => { const a = dayAvail(d); if (a.open && a.start && a.end) { sMin = Math.min(sMin, toMin(a.start)); eMax = Math.max(eMax, toMin(a.end)); } });
    const weekEnd = addDays(days[6], 1);
    appts.forEach((a) => { const s = new Date(a.start), e = new Date(a.end); if (s >= days[0] && s < weekEnd) { sMin = Math.min(sMin, s.getHours() * 60 + s.getMinutes()); eMax = Math.max(eMax, e.getHours() * 60 + e.getMinutes()); } });
    blocks.forEach((b) => { const d = new Date(`${b.date}T00:00:00`); if (d >= days[0] && d < weekEnd) { sMin = Math.min(sMin, toMin(b.start)); eMax = Math.max(eMax, toMin(b.end)); } });
    if (ext) { sMin = Math.min(sMin, earlyHour * 60); eMax = Math.max(eMax, lateHour * 60); }
    const sH = Math.max(0, Math.floor(sMin / 60));
    let eH = Math.min(24, Math.ceil(eMax / 60));
    if (eH <= sH) eH = sH + 1;
    return { startHour: sH, endHour: eH };
  }, [days, dayAvail, appts, blocks, ext, earlyHour, lateHour]);
  const startHour = gridRange.startHour;
  const endHour = gridRange.endHour;
  const startHourRef = useRef(startHour); startHourRef.current = startHour;
  const SLOTS = ((endHour - startHour) * 60) / STEP;
  const slotLabel = useCallback((i) => { const m = startHour * 60 + i * STEP; return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`; }, [startHour]);
  const [now, setNow] = useState(() => new Date());
  const nowLineRef = useRef(null);
  const didAutoScroll = useRef(false);
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t); }, []);
  useEffect(() => {
    if (!isPlanner || view === "month" || didAutoScroll.current) return;
    const t = setTimeout(() => { if (nowLineRef.current) { nowLineRef.current.scrollIntoView({ block: "center", inline: "nearest" }); didAutoScroll.current = true; } }, 500);
    return () => clearTimeout(t);
  }, [isPlanner, view, startHour, endHour]);
  useEffect(() => {
    const f = searchParams.get("focus");
    if (!f) return;
    let d; try { d = parseISO(f); } catch { return; }
    if (!d || isNaN(d.getTime())) return;
    didAutoScroll.current = true;
    setWeekStart(startOfWeek(d, { weekStartsOn: 1 }));
    setView("day");
    setMobileIdx((d.getDay() + 6) % 7);
    const t = setTimeout(() => {
      const di = (d.getDay() + 6) % 7;
      const i = Math.max(0, Math.round(((d.getHours() * 60 + d.getMinutes()) - startHourRef.current * 60) / STEP));
      const el = document.querySelector(`[data-testid="slot-${di}-${i}"]`);
      if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
      searchParams.delete("focus");
      setSearchParams(searchParams, { replace: true });
    }, 700);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchParams.get("assist")) {
      setAssistOpen(true); setAssistResults(null);
      const cust = searchParams.get("customer");
      if (cust) setAssistCustomerId(cust);
      const sp = searchParams; sp.delete("assist"); sp.delete("customer"); setSearchParams(sp, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      const d = r.data || {};
      setShowWeekend(d.show_weekend !== false);
      setExt(!!d.planner_extended_hours);
      setEarlyHour(Math.max(0, Math.min(Number(d.planner_early_hour ?? 7), CORE_START)));
      setLateHour(Math.min(24, Math.max(Number(d.planner_late_hour ?? 21), CORE_END)));
      setMarkExt(d.planner_extended_color !== false);
      setExtColor(d.planner_extended_color_value || "#ECEAF6");
      setDailyMax(Number(d.daily_max_load) || 8);
      setPerDayMax(Array.isArray(d.daily_max_load_by_day) && d.daily_max_load_by_day.length === 7 ? d.daily_max_load_by_day.map(Number) : [0, 0, 0, 0, 0, 0, 0]);
    }).catch(() => {});
  }, []);

  const loadAppts = useCallback(() => {
    const from = toLocalIso(days[0]);
    const to = `${format(addDays(days[6], 1), "yyyy-MM-dd")}T23:59:59`;
    api.get("/admin/appointments", { params: { start: from, end: to } }).then((r) => setAppts(r.data));
  }, [days]);

  const loadBlocks = useCallback(() => {
    const from = format(days[0], "yyyy-MM-dd");
    const to = format(days[6], "yyyy-MM-dd");
    api.get("/admin/blocks", { params: { start: from, end: to } }).then((r) => setBlocks(r.data)).catch(() => {});
  }, [days]);

  const loadCustomers = useCallback(() => api.get("/admin/customers").then((r) => setCustomers(r.data)), []);
  const loadTodos = useCallback(() => api.get("/admin/todos", { params: { include_done: false } }).then((r) => setTodos(r.data)).catch(() => {}), []);
  const loadDayMeta = useCallback(() => {
    const from = format(days[0], "yyyy-MM-dd");
    const to = format(days[6], "yyyy-MM-dd");
    api.get("/admin/day-meta", { params: { start: from, end: to } }).then((r) => {
      const m = {}; (r.data || []).forEach((x) => { m[x.date] = x; }); setDayMeta(m);
    }).catch(() => {});
  }, [days]);

  useEffect(() => {
    api.get("/admin/treatments").then((r) => setTreatments(r.data.filter((t) => t.schedulable && t.active)));
    loadCustomers();
    api.get("/admin/opening-hours").then((r) => setAvail({ hours: r.data.opening_hours, overrides: r.data.overrides }));
  }, [loadCustomers]);
  useEffect(() => { loadAppts(); }, [loadAppts]);
  useEffect(() => { loadBlocks(); }, [loadBlocks]);
  useEffect(() => { loadTodos(); }, [loadTodos]);
  useEffect(() => { loadDayMeta(); }, [loadDayMeta]);
  useEffect(() => {
    if (view !== "month") return;
    const gs = startOfWeek(startOfMonth(monthAnchor), { weekStartsOn: 1 });
    const ge = addDays(gs, 41);
    api.get("/admin/day-meta", { params: { start: format(gs, "yyyy-MM-dd"), end: format(ge, "yyyy-MM-dd") } })
      .then((r) => { const m = {}; (r.data || []).forEach((x) => { m[x.date] = x; }); setDayMeta((prev) => ({ ...prev, ...m })); }).catch(() => {});
  }, [view, monthAnchor]);
  useEffect(() => { try { localStorage.setItem("agendaViewFilter", viewFilter); } catch { /* ignore */ } }, [viewFilter]);
  useEffect(() => { try { localStorage.setItem("agendaView", view); } catch { /* ignore */ } }, [view]);
  useEffect(() => {
    if (view !== "month") return;
    const from = format(startOfMonth(monthAnchor), "yyyy-MM-dd");
    const to = format(endOfMonth(monthAnchor), "yyyy-MM-dd");
    api.get("/admin/appointments", { params: { start: `${from}T00:00:00`, end: `${to}T23:59:59` } }).then((r) => setMonthData((m) => ({ ...m, appts: r.data }))).catch(() => {});
    api.get("/admin/blocks", { params: { start: from, end: to } }).then((r) => setMonthData((m) => ({ ...m, blocks: r.data }))).catch(() => {});
  }, [view, monthAnchor]);
  const cellFromPoint = (x, y) => {
    const el = document.elementFromPoint(x, y);
    const cell = el && el.closest ? el.closest('[data-testid^="slot-"]') : null;
    if (!cell) return null;
    const m = (cell.getAttribute("data-testid") || "").match(/^slot-(\d+)-(\d+)$/);
    return m ? { di: Number(m[1]), i: Number(m[2]) } : null;
  };

  useEffect(() => { try { localStorage.setItem("agendaSelectMode", selectMode ? "1" : "0"); } catch { /* ignore */ } }, [selectMode]);

  const beginTouchSelect = useCallback((day, di, i) => {
    setSelAnchor({ di, i }); setSelCur({ di, i }); setSelDay(day); setSelecting(true);
    touchSelectingRef.current = true;
    try { navigator.vibrate?.(25); } catch { /* ignore */ }
  }, []);

  const onCellTouchStart = useCallback((e, day, di, i, bl) => {
    if (bl || drag || armed || armedRes) return;
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
    if (selectMode) { beginTouchSelect(day, di, i); return; }
    clearTimeout(lpTimer.current);
    lpTimer.current = setTimeout(() => beginTouchSelect(day, di, i), 350);
  }, [selectMode, drag, armed, armedRes, beginTouchSelect]);

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const onMove = (e) => {
      const t = e.touches[0];
      if (!t) return;
      if (touchSelectingRef.current) {
        e.preventDefault();
        const c = cellFromPoint(t.clientX, t.clientY);
        if (c) setSelCur(c);
      } else if (lpTimer.current && touchStartRef.current) {
        const dx = Math.abs(t.clientX - touchStartRef.current.x);
        const dy = Math.abs(t.clientY - touchStartRef.current.y);
        if (dx > 8 || dy > 8) { clearTimeout(lpTimer.current); lpTimer.current = null; }
      }
    };
    const onEnd = () => { clearTimeout(lpTimer.current); lpTimer.current = null; touchSelectingRef.current = false; touchStartRef.current = null; };
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    return () => { el.removeEventListener("touchmove", onMove); el.removeEventListener("touchend", onEnd); el.removeEventListener("touchcancel", onEnd); };
  }, [selectMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selecting) return;
    const onUp = () => {
      setSelecting(false);
      if (!selAnchor || !selCur) return;
      const diLo = Math.min(selAnchor.di, selCur.di), diHi = Math.max(selAnchor.di, selCur.di);
      const lo = Math.min(selAnchor.i, selCur.i), hi = Math.max(selAnchor.i, selCur.i);
      if (hi === lo && diLo === diHi) return; // single tap -> handled by onClick
      justSelectedRef.current = true;
      setTimeout(() => { justSelectedRef.current = false; }, 350);
      const selDays = [];
      for (let d = diLo; d <= diHi; d++) {
        const cell = document.querySelector(`[data-testid^="slot-${d}-"][data-date]`);
        if (cell) selDays.push(parseISO(cell.getAttribute("data-date")));
      }
      if (!selDays.length) return;
      openSlotAction(selDays[0], lo, hi + 1, selDays);
    };
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => { window.removeEventListener("mouseup", onUp); window.removeEventListener("touchend", onUp); };
  }, [selecting, selAnchor, selCur]); // eslint-disable-line react-hooks/exhaustive-deps

  const blocksForDay = useCallback((date) => {
    const key = format(date, "yyyy-MM-dd");
    return blocks.filter((b) => b.date === key);
  }, [blocks]);
  const slotBlocked = useCallback((date, i) => {
    const m = startHour * 60 + i * STEP;
    return blocksForDay(date).some((b) => toMin(b.start) <= m && m < toMin(b.end));
  }, [blocksForDay]);

  const slotOpen = useCallback((av, i) => {
    if (!av.open) return false;
    const m = startHour * 60 + i * STEP;
    return m >= toMin(av.start) && m < toMin(av.end);
  }, [startHour]);

  const slotStart = useCallback((day, slotIndex) => {
    const start = new Date(day);
    start.setHours(startHour, 0, 0, 0);
    start.setMinutes(slotIndex * STEP);
    return start;
  }, [startHour]);

  const handleDrop = async (day, slotIndex) => {
    if (!drag) return;
    const start = slotStart(day, slotIndex);
    if (slotBlocked(day, slotIndex)) {
      toast.error("Dit tijdvak is geblokkeerd.");
      setDrag(null);
      return;
    }
    const closedDay = !slotOpen(dayAvail(day), slotIndex);
    if (drag.type === "todo") {
      try {
        await api.post(`/admin/todos/${drag.id}/schedule`, { start: toLocalIso(start) });
        toast.success("To-do op de agenda gezet.");
        loadTodos(); loadBlocks();
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
      setDrag(null); return;
    }
    if (drag.type === "block") {
      const b = blocks.find((x) => x.id === drag.id);
      if (b) {
        const durMin = toMin(b.end) - toMin(b.start);
        const startM = startHour * 60 + slotIndex * STEP;
        const endM = startM + durMin;
        const newStart = `${pad(Math.floor(startM / 60))}:${pad(startM % 60)}`;
        const newEnd = `${pad(Math.floor(endM / 60))}:${pad(endM % 60)}`;
        try {
          await api.put(`/admin/blocks/${b.id}`, { date: format(day, "yyyy-MM-dd"), start: newStart, end: newEnd });
          loadBlocks(); loadTodos();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
      }
      setDrag(null); return;
    }
    if (drag.type === "treatment") {
      const t = treatments.find((x) => x.id === drag.id);
      setModal({ start, closedDay });
      setApptTreatmentId(t.id);
      setCustomerId(""); setNotes(""); setWarnings([]);
    } else if (drag.type === "appt") {
      const appt = appts.find((a) => a.id === drag.id);
      if (appt) {
        const newIso = toLocalIso(start);
        if (newIso === appt.start) { setDrag(null); return; }
        setRescheduleDrag({ appt, start, closedDay });
      }
    }
    setDrag(null);
  };

  const confirmRescheduleDrag = async () => {
    if (!rescheduleDrag) return;
    const { appt, start } = rescheduleDrag;
    try {
      await api.post(`/admin/appointments/${appt.id}/propose-reschedule`, { start: toLocalIso(start), notify: true });
      toast.success("Verzetvoorstel verstuurd. De klant ontvangt een bevestigingslink.");
      setRescheduleDrag(null);
      loadAppts();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const moveApptDirect = async () => {
    if (!rescheduleDrag) return;
    const { appt, start } = rescheduleDrag;
    try {
      await api.put(`/admin/appointments/${appt.id}`, { start: toLocalIso(start) });
      toast.success("Afspraak verplaatst (zonder klant te informeren).");
      setRescheduleDrag(null);
      loadAppts();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openSlotAction = (day, slotIndex, endIndex = null, selDays = null) => setSlotAction({ day, days: selDays || [day], slotIndex, endIndex, closedDay: !slotOpen(dayAvail(day), slotIndex) });
  const startApptFromSlot = () => {
    const { day, slotIndex, closedDay } = slotAction;
    setModal({ start: slotStart(day, slotIndex), closedDay });
    setApptTreatmentId(""); setCustomerId(""); setNotes(""); setWarnings([]);
    setSlotAction(null);
  };
  const startBlockFromSlot = () => {
    const { day, days: sd, slotIndex, endIndex } = slotAction;
    openBlock(day, slotIndex, endIndex, (sd || [day]).map((d) => format(d, "yyyy-MM-dd")));
    setSlotAction(null);
  };
  const startPrivateFromSlot = () => {
    const { day, days: sd, slotIndex, endIndex } = slotAction;
    openPrivate(day, slotIndex, endIndex, (sd || [day]).map((d) => format(d, "yyyy-MM-dd")));
    setSlotAction(null);
  };

  const tapSlot = (day, slotIndex) => {
    if (slotBlocked(day, slotIndex)) { toast.error("Dit tijdvak is geblokkeerd."); return; }
    const closedDay = !slotOpen(dayAvail(day), slotIndex);
    if (armedRes) {
      setModal({ start: slotStart(day, slotIndex), closedDay, reservationId: armedRes.reservationId });
      setApptTreatmentId(armedRes.treatmentId || "");
      setCustomerId(armedRes.customerId);
      setNotes(armedRes.notes || "");
      setWarnings([]);
      return;
    }
    if (armed) {
      setModal({ start: slotStart(day, slotIndex), closedDay });
      setApptTreatmentId(armed);
      setCustomerId(""); setNotes(""); setWarnings([]);
      setArmed(null);
    } else {
      openSlotAction(day, slotIndex);
    }
  };

  // In de Planner-app vereist een leeg tijdvak een dubbeltik (voorkomt per ongeluk openen tijdens scrollen).
  const handleSlotTap = (day, i, di) => {
    if (!isPlanner || armed || armedRes) { tapSlot(day, i); return; }
    const now = Date.now();
    const lt = lastTapRef.current;
    if (lt.di === di && lt.i === i && now - lt.t < 450) {
      lastTapRef.current = { t: 0, di: null, i: null };
      tapSlot(day, i);
    } else {
      lastTapRef.current = { t: now, di, i };
    }
  };

  const confirmAppt = async (force = false) => {
    const treatment = treatments.find((t) => t.id === apptTreatmentId);
    if (!treatment) { toast.error("Kies een behandeling."); return; }
    if (!customerId) { toast.error("Kies een klant."); return; }
    if (!force) {
      const warns = computeRestWarnings(treatment, modal.start, appts);
      const tLoad = (treatment.load || 0) || loadFromDur(treatment.duration_min || 30);
      const dLoad = dayLoadFor(modal.start);
      const dMax = maxForDay(modal.start);
      if (dLoad + tLoad > dMax) warns.push(`Deze afspraak brengt de belasting op ${dLoad + tLoad}/${dMax} — boven je maximum voor deze dag.`);
      if (warns.length) { setWarnings(warns); return; }
    }
    try {
      await api.post("/admin/appointments", {
        customer_id: customerId,
        treatment_id: treatment.id,
        start: toLocalIso(modal.start),
        notes,
        send_confirmation: true,
      });
      const sc = customers.find((c) => c.id === customerId);
      const hasContact = sc && (sc.email || sc.phone);
      toast.success(hasContact ? "Afspraak ingepland. Bevestiging verstuurd." : "Afspraak ingepland. (Geen contactgegevens — geen bevestiging verstuurd.)");
      if (modal?.reservationId) {
        try { await api.put(`/admin/reservations/${modal.reservationId}`, null, { params: { status: "handled" } }); } catch (e) {}
        setArmedRes(null);
      }
      setModal(null); setWarnings([]); loadAppts();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const confirmOfferAppt = async (id) => {
    try {
      await api.post(`/admin/appointments/${id}/confirm-offer`);
      toast.success("Afspraak bevestigd. Klant is geïnformeerd.");
      setDetail(null); loadAppts();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const removeAppt = async (id) => {    await api.delete(`/admin/appointments/${id}`);
    toast.success("Afspraak verwijderd.");
    setDetail(null); loadAppts();
  };

  const openBlock = (day, slotIndex, endIndex = null, dates = null) => {
    const start = slotLabel(slotIndex);
    const endMin = endIndex != null ? startHour * 60 + endIndex * STEP : Math.min(startHour * 60 + slotIndex * STEP + 60, endHour * 60); // default 1 uur
    const end = `${pad(Math.floor(endMin / 60))}:${pad(endMin % 60)}`;
    setBlockModal({ date: format(day, "yyyy-MM-dd"), dates, start, end, note: "" });
  };
  const openBlockWholeDay = (day) => {
    const av = dayAvail(day);
    const start = av.open ? av.start : "09:00";
    const end = av.open ? av.end : "18:00";
    setBlockModal({ date: format(day, "yyyy-MM-dd"), start, end, note: "Hele dag geblokkeerd" });
  };
  const openApptForDay = (day) => {
    const av = dayAvail(day);
    const start = new Date(day);
    const [h, m] = (av.open ? av.start : "09:00").split(":").map(Number);
    start.setHours(h || 9, m || 0, 0, 0);
    setApptTreatmentId(""); setCustomerId(""); setNotes(""); setWarnings([]);
    setModal({ start, closedDay: !av.open });
  };
  const openBlockForDay = (day) => {
    const av = dayAvail(day);
    const start = av.open ? av.start : "09:00";
    const endMin = Math.min(toMin(start) + 60, av.open ? toMin(av.end) : 18 * 60);
    const end = `${pad(Math.floor(endMin / 60))}:${pad(endMin % 60)}`;
    setBlockModal({ date: format(day, "yyyy-MM-dd"), start, end, note: "" });
  };
  // Waarschuwing als een privé-afspraak/blokkade de dag boven de max. belasting brengt.
  const loadWarnFor = (dates, start, end, loadOverride = 0) => {
    const dur = toMin(end) - toMin(start);
    if (dur <= 0) return [];
    const add = loadOverride || loadFromDur(dur);
    const warns = [];
    (dates || []).forEach((ds) => {
      const dt = parseISO(`${ds}T00:00:00`);
      const cur = dayLoadFor(dt);
      const mx = maxForDay(dt);
      if (cur + add > mx) warns.push(`${format(dt, "EEEE d MMM", { locale: nl })}: belasting wordt ${cur + add}/${mx} — boven je maximum voor deze dag.`);
    });
    return warns;
  };

  const saveBlock = async (force = false) => {
    if (toMin(blockModal.end) <= toMin(blockModal.start)) { toast.error("Eindtijd moet na de starttijd liggen."); return; }
    const dates = blockModal.dates && blockModal.dates.length ? blockModal.dates : [blockModal.date];
    const load = Number(blockModal.load) || 0;
    if (load && !force) {
      const warns = loadWarnFor(dates, blockModal.start, blockModal.end, load);
      if (warns.length) { setBlockWarn(warns); return; }
    }
    try {
      if (blockModal.id) {
        await api.put(`/admin/blocks/${blockModal.id}`, { date: blockModal.date, start: blockModal.start, end: blockModal.end, note: blockModal.note, load });
        toast.success("Blokkering bijgewerkt.");
      } else {
        await Promise.all(dates.map((d) => api.post("/admin/blocks", { date: d, start: blockModal.start, end: blockModal.end, note: blockModal.note, load })));
        toast.success(dates.length > 1 ? `${dates.length} dagen geblokkeerd.` : "Tijd geblokkeerd.");
      }
      setBlockModal(null); setBlockWarn([]); loadBlocks();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const openBlockEdit = (b) => setBlockModal({ id: b.id, date: b.date, start: b.start, end: b.end, note: b.note || "", load: b.load || 0 });
  const removeBlock = async (b) => {
    if (b.kind === "todo" && b.todo_id) {
      await api.post(`/admin/todos/${b.todo_id}/unschedule`);
      toast.success("Van agenda gehaald (blijft in je to-do's).");
    } else {
      await api.delete(`/admin/blocks/${b.id}`);
      toast.success("Verwijderd.");
    }
    loadBlocks(); loadTodos();
  };

  const openPrivate = (day, slotIndex, endIndex = null, dates = null) => {
    const start = slotLabel(slotIndex);
    const endMin = endIndex != null ? startHour * 60 + endIndex * STEP : Math.min(startHour * 60 + slotIndex * STEP + 60, endHour * 60);
    const end = `${pad(Math.floor(endMin / 60))}:${pad(endMin % 60)}`;
    setPrivateModal({ date: format(day, "yyyy-MM-dd"), dates, start, end, title: "", color: TODO_COLORS[4].value, load: 0 });
  };
  const savePrivate = async (force = false) => {
    if (!privateModal.title.trim()) { toast.error("Geef de privé afspraak een titel."); return; }
    if (toMin(privateModal.end) <= toMin(privateModal.start)) { toast.error("Eindtijd moet na de starttijd liggen."); return; }
    const dates = privateModal.dates && privateModal.dates.length ? privateModal.dates : [privateModal.date];
    if (!force && !privateModal.id) {
      const warns = loadWarnFor(dates, privateModal.start, privateModal.end, Number(privateModal.load) || 0);
      if (warns.length) { setPrivWarn(warns); return; }
    }
    const body = { date: privateModal.date, start: privateModal.start, end: privateModal.end, title: privateModal.title, color: privateModal.color, load: Number(privateModal.load) || 0 };
    try {
      if (privateModal.id) {
        await api.put(`/admin/blocks/${privateModal.id}`, body);
        toast.success("Privé afspraak bijgewerkt.");
      } else {
        await Promise.all(dates.map((d) => api.post("/admin/blocks", { ...body, date: d, kind: "private", note: "" })));
        toast.success(dates.length > 1 ? `${dates.length} privé afspraken toegevoegd.` : "Privé afspraak toegevoegd.");
      }
      setPrivateModal(null); setPrivWarn([]); loadBlocks();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const openPrivateEdit = (b) => setPrivateModal({ id: b.id, date: b.date, start: b.start, end: b.end, title: b.title || "", color: b.color || TODO_COLORS[4].value, load: b.load || 0 });

  const toggleDetailSub = async (subId) => {
    if (!todoDetail) return;
    const subtasks = (todoDetail.subtasks || []).map((st) => (st.id === subId ? { ...st, done: !st.done } : st));
    setTodoDetail({ ...todoDetail, subtasks });
    await api.put(`/admin/todos/${todoDetail.id}`, { subtasks });
    loadTodos();
  };
  const finishTodo = async () => {
    if (!todoDetail) return;
    await api.put(`/admin/todos/${todoDetail.id}`, { done: true });
    toast.success(todoDetail.scheduled_start ? "To-do afgerond en uit agenda gehaald" : "To-do afgerond");
    setTodoDetail(null); loadTodos(); loadBlocks();
  };

  const shareAgenda = async () => {
    try {
      const { data } = await api.get("/admin/agenda-share");
      try { await navigator.clipboard.writeText(data.url); } catch (e) {}
      toast.success("Deel-link gekopieerd", { description: "Toont alleen behandelingen · " + data.url, duration: 6000 });
      window.open(data.url, "_blank");
    } catch (err) { toast.error("Kon deel-link niet ophalen."); }
  };

  const grouped = useMemo(() => {
    const cats = {};
    treatments.forEach((t) => { (cats[t.category] ||= []).push(t); });
    return cats;
  }, [treatments]);
  const openTodos = useMemo(() => todos.filter((t) => !t.done), [todos]);
  // Dag-to-do's: gekoppeld aan een dag (due_date) maar zonder tijdblok (niet ingepland).
  const dayTodos = (day) => { const ds = format(day, "yyyy-MM-dd"); return openTodos.filter((t) => !t.scheduled_start && t.due_date === ds); };
  const assignTodoToDay = async (day) => {
    if (!drag || drag.type !== "todo") { setAllDayOver(null); return; }
    try {
      await api.post(`/admin/todos/${drag.id}/assign-day`, { date: format(day, "yyyy-MM-dd") });
      toast.success("To-do aan deze dag gekoppeld (zonder tijd).");
      loadTodos(); loadBlocks();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setDrag(null); setAllDayOver(null);
  };
  const unassignTodoDay = async (t) => {
    await api.put(`/admin/todos/${t.id}`, { due_date: "" });
    toast.success("To-do losgekoppeld van de dag.");
    loadTodos();
  };
  const renderAllDayChip = (t) => (
    <div key={t.id} data-testid={`allday-todo-${t.id}`} onClick={() => setTodoDetail(t)} title={`${t.title} · belasting ${(t.load || 0) || loadFromDur(t.duration_min || 30)}`}
      className="group/ad flex items-center gap-1 rounded-md px-1.5 py-0.5 cursor-pointer max-w-full" style={{ backgroundColor: t.color || "#93B5C6" }}>
      <CheckSquare className="w-2.5 h-2.5 shrink-0" style={{ color: onColor }} />
      <span className="text-[10px] truncate flex-1 min-w-0" style={{ color: onColor }}>{t.title}</span>
      <button data-testid={`allday-remove-${t.id}`} onClick={(e) => { e.stopPropagation(); unassignTodoDay(t); }}
        className="opacity-0 group-hover/ad:opacity-100 transition-opacity shrink-0"><X className="w-2.5 h-2.5" style={{ color: onColor }} /></button>
    </div>
  );

  useEffect(() => {
    const idx = days.findIndex((d) => isSameDay(d, new Date()));
    setMobileIdx(idx >= 0 ? idx : 0);
  }, []); // run once on mount

  const renderDayColumn = (day, di) => {
    const showTreatments = viewFilter !== "private";
    const showPrivate = viewFilter !== "treatments";
    const dayAppts = showTreatments ? appts.filter((a) => isSameDay(parseISO(a.start), day)) : [];
    const av = dayAvail(day);
    return (
      <div key={di} className="relative border-r border-[#F2EBE1] last:border-r-0 select-none" style={{ height: SLOTS * SLOT_H }}>
        {Array.from({ length: SLOTS }).map((_, i) => {
          const open = slotOpen(av, i);
          const bl = slotBlocked(day, i);
          const isOver = drag && dragOver && dragOver.di === di && dragOver.i === i && !bl;
          const inSel = selecting && selAnchor && selCur && di >= Math.min(selAnchor.di, selCur.di) && di <= Math.max(selAnchor.di, selCur.di) && i >= Math.min(selAnchor.i, selCur.i) && i <= Math.max(selAnchor.i, selCur.i);
          const extPaint = (ext && markExt) && !open && !bl && !inSel && !isOver;
          return (
            <div key={i} data-testid={`slot-${di}-${i}`} data-date={format(day, "yyyy-MM-dd")}
              onDragOver={(e) => { e.preventDefault(); if (drag && !bl) setDragOver({ di, i }); }}
              onDrop={() => { handleDrop(day, i); setDragOver(null); }}
              onMouseDown={(e) => { if (!bl && !drag && !armed && !armedRes) { e.preventDefault(); setSelAnchor({ di, i }); setSelCur({ di, i }); setSelDay(day); setSelecting(true); } }}
              onMouseEnter={() => { if (selecting && selAnchor) setSelCur({ di, i }); }}
              onTouchStart={(e) => onCellTouchStart(e, day, di, i, bl)}
              onClick={() => { if (!bl && !drag && !justSelectedRef.current) handleSlotTap(day, i, di); }}
              style={{ height: SLOT_H, backgroundColor: extPaint ? extColor : undefined, touchAction: (bl || drag || armed || armedRes) ? undefined : (selectMode ? "none" : "pan-y") }}
              className={`border-b border-[#F5F0E8] transition-colors ${
                inSel ? "bg-brand-light ring-1 ring-inset ring-brand/40"
                : bl ? (!open ? "bg-[#F7F3EE]" : "")
                : isOver ? `ring-2 ring-inset ${open ? "bg-brand-light ring-brand" : "bg-amber-100 ring-amber-400"}`
                : !open ? ((drag || armed) ? `${(ext && markExt) ? "" : "bg-[#F7F3EE]"} cursor-pointer hover:bg-amber-100/70` : ((ext && markExt) ? "cursor-pointer" : "bg-[#F7F3EE] cursor-pointer hover:bg-[#F0E9DF]"))
                : drag ? "hover:bg-brand-light/60"
                : armed ? "cursor-pointer bg-brand-light/20 hover:bg-brand-light ring-1 ring-inset ring-brand/20"
                : "cursor-pointer hover:bg-cream-subtle/60"}`} />
          );
        })}
        {drag && dragOver && dragOver.di === di && (() => {
          const prevT = drag.type === "treatment" ? treatments.find((t) => t.id === drag.id) : null;
          const prevA = drag.type === "appt" ? appts.find((a) => a.id === drag.id) : null;
          const prevTodo = drag.type === "todo" ? todos.find((t) => t.id === drag.id) : null;
          const prevBlock = drag.type === "block" ? blocks.find((b) => b.id === drag.id) : null;
          const dur = prevT?.duration_min || prevA?.duration_min || prevTodo?.duration_min || (prevBlock ? (toMin(prevBlock.end) - toMin(prevBlock.start)) : STEP);
          const label = prevT?.name || prevA?.treatment_name || prevTodo?.title || prevBlock?.title || "Blok";
          const top = dragOver.i * SLOT_H;
          const h = Math.max((dur / STEP) * SLOT_H - 2, 20);
          return (
            <div style={{ top: top + 1, height: h, left: 3, right: 3 }}
              className="absolute rounded-lg px-2 py-0.5 bg-brand/30 border-2 border-brand border-dashed pointer-events-none z-10 overflow-hidden">
              <div className="text-[11px] font-semibold text-brand-hover leading-tight">{slotLabel(dragOver.i)}</div>
              <div className="text-[10px] text-brand-hover/80 truncate">{label} · {dur} min</div>
            </div>
          );
        })()}
        {(showPrivate ? blocksForDay(day) : []).map((b) => {
          const top = ((toMin(b.start) - startHour * 60) / STEP) * SLOT_H;
          const height = Math.max(((toMin(b.end) - toMin(b.start)) / STEP) * SLOT_H - 2, 20);
          const colored = b.kind === "private" || b.kind === "todo";
          const todoRef = b.kind === "todo" ? todos.find((x) => x.id === b.todo_id) : null;
          const openSteps = todoRef && todoRef.subtasks ? todoRef.subtasks.filter((st) => !st.done).length : 0;
          if (colored) {
            return (
              <div key={b.id} draggable data-testid={`block-${b.id}`}
                onDragStart={() => setDrag({ type: "block", id: b.id })}
                onDragEnd={() => { setDrag(null); setDragOver(null); }}
                onClick={() => {
                  if (b.kind === "private") openPrivateEdit(b);
                  else if (b.kind === "todo") { const td = todos.find((x) => x.id === b.todo_id); if (td) setTodoDetail(td); }
                }}
                title={b.title || (b.kind === "todo" ? "To-do" : "Privé")}
                style={{ top: top + 1, height, left: 3, right: 3, backgroundColor: b.color || "#93B5C6" }}
                className={`absolute rounded-lg px-2 py-1 overflow-hidden border border-black/5 active:cursor-grabbing shadow-sm group/block cursor-pointer`}>
                <div className="flex items-center gap-1 text-[10px] font-semibold" style={{ color: onColor }}>
                  {b.kind === "todo" ? <CheckSquare className="w-3 h-3" /> : <UserRound className="w-3 h-3" />}
                  {b.start}–{b.end}
                </div>
                <div className="text-[11px] truncate" style={{ color: onColor }}>{b.title || (b.kind === "todo" ? "To-do" : "Privé")}</div>
                {openSteps > 0 && (
                  <div className="text-[10px] flex items-center gap-0.5 mt-0.5 font-medium" style={{ color: onColor }} data-testid={`block-open-steps-${b.id}`}>
                    <ListChecks className="w-2.5 h-2.5" /> {openSteps} open {openSteps === 1 ? "stap" : "stappen"}
                  </div>
                )}
                <button data-testid={`remove-block-${b.id}`} onClick={(e) => { e.stopPropagation(); removeBlock(b); }}
                  className="absolute top-1 right-1 p-0.5 rounded-full bg-white/70 opacity-0 group-hover/block:opacity-100 hover:text-destructive transition-opacity">
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          }
          return (
            <div key={b.id} data-testid={`block-${b.id}`}
              title={b.note || "Geblokkeerd"}
              draggable
              onDragStart={() => setDrag({ type: "block", id: b.id })}
              onDragEnd={() => { setDrag(null); setDragOver(null); }}
              onClick={() => openBlockEdit(b)}
              style={{ top: top + 1, height, left: 3, right: 3,
                backgroundImage: "repeating-linear-gradient(45deg,#E7DFD6,#E7DFD6 6px,#F1EBE3 6px,#F1EBE3 12px)" }}
              className="absolute rounded-lg px-2 py-1 overflow-hidden border border-[#DAD0C4] group/block cursor-grab active:cursor-grabbing">
              <div className="flex items-center gap-1 text-[10px] text-ink-muted font-medium">
                <Ban className="w-3 h-3" /> Geblokkeerd
                {b.load > 0 && (
                  <span data-testid={`block-load-badge-${b.id}`} title={`Belasting ${b.load}`}
                    className="ml-auto inline-flex items-center gap-0.5 px-1 rounded-full bg-brand text-white text-[9px] font-semibold leading-none py-0.5">
                    <Activity className="w-2 h-2" />{b.load}
                  </span>
                )}
              </div>
              {b.note && <div className="text-[10px] text-ink-muted truncate">{b.note}</div>}
              <button data-testid={`remove-block-${b.id}`} onClick={(e) => { e.stopPropagation(); removeBlock(b); }}
                className="absolute top-1 right-1 p-0.5 rounded-full bg-white/70 opacity-0 group-hover/block:opacity-100 hover:text-destructive transition-opacity">
                <X className="w-3 h-3" />
              </button>
            </div>
          );
        })}
        {dayAppts.map((a) => {
          const s = parseISO(a.start);
          const mins = s.getHours() * 60 + s.getMinutes() - startHour * 60;
          const top = (mins / STEP) * SLOT_H;
          const height = Math.max((a.duration_min / STEP) * SLOT_H - 4, 22);
          const cancelled = a.status === "cancelled";
          const noShow = a.status === "no_show";
          const pending = a.status === "reschedule_pending" || a.status === "proposal_pending" || a.status === "offer_pending";
          const isOffer = a.status === "offer_pending";
          const isProposal = a.status === "proposal_pending" || a.status === "offer_pending";
          const inactive = cancelled || noShow;
          const endD = a.end ? parseISO(a.end) : new Date(s.getTime() + (a.duration_min || 0) * 60000);
          const timeRange = `${format(s, "HH:mm")}–${format(endD, "HH:mm")}`;
          const statusLabel = cancelled ? "Geannuleerd" : noShow ? "Niet verschenen" : a.status === "offer_pending" ? "Aanbod geclaimd · bevestigen" : a.status === "proposal_pending" ? "Voorstel · wacht op akkoord" : (a.status === "reschedule_pending") ? "Verzet aangevraagd" : "";
          const restMin = a.rest_min || 0;
          const durH = (a.duration_min / STEP) * SLOT_H;
          const restTop = top + 2 + durH;
          const restHeight = (restMin / STEP) * SLOT_H - 2;
          const showRest = restMin > 0 && !cancelled && !noShow;
          return (
            <Fragment key={a.id}>
            {showRest && (
              <div data-testid={`rest-${a.id}`} title={`Rusttijd ${restMin} min`}
                style={{ top: restTop, height: Math.max(restHeight, 8), left: 3, right: 3,
                  backgroundImage: "repeating-linear-gradient(45deg,#D9D2E6 0,#D9D2E6 5px,#ECE7F3 5px,#ECE7F3 10px)" }}
                className="absolute rounded-md pointer-events-none overflow-hidden opacity-80 border border-[#C9BFDD]/50">
                {restHeight >= 14 && <div className="text-[9px] text-[#6E6188] px-1.5 pt-0.5 truncate">Rust {restMin}m</div>}
              </div>
            )}
            <div draggable={!inactive} data-testid={`appt-${a.id}`}
              onDragStart={() => !inactive && setDrag({ type: "appt", id: a.id })}
              onDragEnd={() => { setDrag(null); setDragOver(null); }}
              onClick={() => setDetail(a)}
              style={{ top: top + 2, height, left: 3, right: 3,
                ...(isOffer ? { backgroundImage: "repeating-linear-gradient(45deg, #9BD3B0 0, #9BD3B0 6px, #DCF0E4 6px, #DCF0E4 12px)" } : pending ? { backgroundImage: "repeating-linear-gradient(45deg, #F6D89B 0, #F6D89B 6px, #FBEBD2 6px, #FBEBD2 12px)" } : {}) }}
              className={`absolute rounded-lg px-2 py-1 overflow-hidden cursor-pointer shadow-sm ${
                cancelled ? "bg-[#EBE6E0] text-ink-muted line-through opacity-70"
                : noShow ? "bg-[#F3E7D3] text-[#8A6A2F] border border-[#E4C99B]"
                : isOffer ? "text-[#1F6B44] border-2 border-dashed border-[#3E9E6C]"
                : pending ? "text-[#9A5B12] border-2 border-dashed border-[#D98A2B]"
                : "bg-brand text-white hover:bg-brand-hover"}`}>
              <div className="text-[10px] font-semibold leading-tight truncate flex items-center gap-1">
                {isOffer ? <Sparkles className="w-2.5 h-2.5 shrink-0" strokeWidth={2.5} /> : pending && <RotateCw className="w-2.5 h-2.5 shrink-0" strokeWidth={2.5} />}
                {timeRange}
              </div>
              <div className="text-[11px] font-medium leading-tight truncate">{a.customer_name}</div>
              <div className="text-[10px] opacity-90 truncate">{statusLabel ? `${statusLabel} · ${a.treatment_name}` : a.treatment_name}</div>
            </div>
            </Fragment>
          );
        })}
        {isSameDay(day, now) && (() => {
          const nowMin = now.getHours() * 60 + now.getMinutes();
          if (nowMin < startHour * 60 || nowMin > endHour * 60) return null;
          const top = ((nowMin - startHour * 60) / STEP) * SLOT_H;
          return (
            <div ref={nowLineRef} data-testid="now-line" className="absolute left-0 right-0 pointer-events-none z-20" style={{ top }}>
              <div className="relative h-0">
                <div className="h-[2px] bg-[#EF4444]" />
                <div className="absolute -left-[3px] -top-[3px] w-2 h-2 rounded-full bg-[#EF4444] shadow-sm" />
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  const goToDay = (d) => {
    const ws = startOfWeek(d, { weekStartsOn: 1 });
    setWeekStart(ws);
    const idx = Math.round((d - ws) / 86400000);
    setMobileIdx(Math.max(0, Math.min(6, idx)));
    setView("day");
  };
  const openClosedDay = async (day) => {
    if (!day) return;
    const ds = format(day, "yyyy-MM-dd");
    try {
      await api.post("/admin/overrides", { date: ds, end_date: "", mode: "open", start: "09:00", end: "18:00", note: "Eenmalig geopend" });
      const r = await api.get("/admin/opening-hours");
      setAvail({ hours: r.data.opening_hours, overrides: r.data.overrides });
      toast.success("Dag eenmalig geopend.");
      setExceptionDay(null);
      setDayDetail(null);
      goToDay(day);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const goPrev = () => {
    if (view === "day") { if (mobileIdx > 0) setMobileIdx(mobileIdx - 1); else { setWeekStart(addWeeks(weekStart, -1)); setMobileIdx(6); } }
    else if (view === "week") setWeekStart(addWeeks(weekStart, -1));
    else setMonthAnchor(addMonths(monthAnchor, -1));
  };
  const goNext = () => {
    if (view === "day") { if (mobileIdx < 6) setMobileIdx(mobileIdx + 1); else { setWeekStart(addWeeks(weekStart, 1)); setMobileIdx(0); } }
    else if (view === "week") setWeekStart(addWeeks(weekStart, 1));
    else setMonthAnchor(addMonths(monthAnchor, 1));
  };
  const goToday = () => {
    const now = new Date();
    const ws = startOfWeek(now, { weekStartsOn: 1 });
    setWeekStart(ws); setMonthAnchor(startOfMonth(now));
    const idx = Math.round((now - ws) / 86400000);
    setMobileIdx(Math.max(0, Math.min(6, idx)));
  };
  const navLabel = view === "day" ? format(days[mobileIdx], "EEEE d MMM yyyy", { locale: nl })
    : view === "week" ? `${format(days[0], "d MMM", { locale: nl })} – ${format(days[6], "d MMM yyyy", { locale: nl })}`
    : format(monthAnchor, "MMMM yyyy", { locale: nl });

  const ddStr = dayDetail ? format(dayDetail, "yyyy-MM-dd") : "";
  const ddAppts = dayDetail ? monthData.appts.filter((a) => a.status !== "cancelled" && (a.start || "").slice(0, 10) === ddStr).sort((x, y) => (x.start || "").localeCompare(y.start || "")) : [];
  const ddPriv = dayDetail ? monthData.blocks.filter((b) => b.kind === "private" && b.date === ddStr) : [];
  const ddTodos = dayDetail ? todos.filter((t) => !t.done && (t.due_date === ddStr || (t.scheduled_start || "").slice(0, 10) === ddStr)) : [];
  const ddOpen = dayDetail ? dayAvail(dayDetail).open : true;

  const renderMonth = () => {
    const mStart = startOfMonth(monthAnchor);
    const gridStart = startOfWeek(mStart, { weekStartsOn: 1 });
    const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
    const dow = ["ma", "di", "wo", "do", "vr", "za", "zo"];
    return (
      <div className="p-2 sm:p-3" data-testid="agenda-month">
        <div className="grid grid-cols-7 mb-1">
          {dow.map((d) => (<div key={d} className="text-center text-[11px] uppercase tracking-wide text-ink-muted py-1">{d}</div>))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((day) => {
            const inMonth = isSameMonth(day, mStart);
            const today = isSameDay(day, new Date());
            const av = dayAvail(day);
            const ds = format(day, "yyyy-MM-dd");
            const nApp = monthData.appts.filter((a) => a.status !== "cancelled" && (a.start || "").slice(0, 10) === ds).length;
            const nPriv = monthData.blocks.filter((b) => b.kind === "private" && b.date === ds).length;
            const nTodo = todos.filter((t) => !t.done && (t.due_date === ds || (t.scheduled_start || "").slice(0, 10) === ds)).length;
            const total = nApp + nPriv + nTodo;
            const mLoad = monthData.appts.filter((a) => a.status !== "cancelled" && (a.start || "").slice(0, 10) === ds).reduce((sm, a) => sm + loadFromDur(a.duration_min || 30), 0)
              + monthData.blocks.filter((b) => (b.kind === "private" || b.kind === "todo" || b.todo_id) && b.date === ds).reduce((sm, b) => { try { const [h1, m1] = b.start.split(":").map(Number); const [h2, m2] = b.end.split(":").map(Number); const dl = loadFromDur((h2 * 60 + m2) - (h1 * 60 + m1)); return sm + ((b.kind === "private" && b.load) ? b.load : dl); } catch { return sm; } }, 0)
              + todos.filter((t) => !t.done && !t.scheduled_start && t.due_date === ds).reduce((sm, t) => sm + ((t.load || 0) || loadFromDur(t.duration_min || 30)), 0);
            const mMax = maxForDay(day);
            const over = av.open && mLoad > mMax;
            const busyBg = !av.open ? "bg-[#F7F3EE]"
              : over ? "bg-[#F6DCDF]"
              : total >= 5 ? "bg-[#F6D89B]"
              : total >= 3 ? "bg-[#FBEBD2]"
              : "bg-white";
            return (
              <button key={ds} data-testid={`month-day-${ds}`} onClick={() => setDayDetail(day)}
                title={over ? `Overvol — belasting ${mLoad}/${mMax}` : total > 0 ? `${total} ingepland` : undefined}
                className={`min-h-[64px] sm:min-h-[88px] rounded-xl border p-1.5 text-left flex flex-col transition-colors ${over ? "border-[#C2607A] ring-1 ring-[#C2607A]" : today ? "border-brand ring-1 ring-brand" : "border-[#F2EBE1] hover:border-brand/40"} ${!inMonth ? "opacity-40" : ""} ${busyBg}`}>
                <div className="flex items-center justify-between">
                  <div className={`text-sm font-display ${today ? "text-brand" : ""}`}>{format(day, "d")}</div>
                  <div className="flex items-center gap-1">
                    {dayMeta[ds]?.summary && <StickyNote className="w-3 h-3 text-brand fill-brand/20" data-testid={`month-note-${ds}`}><title>{dayMeta[ds].summary}</title></StickyNote>}
                    {over ? <AlertTriangle className="w-3.5 h-3.5 text-[#C2607A]" data-testid={`month-overload-${ds}`} />
                      : !av.open && <Lock className="w-3 h-3 text-ink-muted/50" data-testid={`month-closed-${ds}`} />}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-auto">
                  {nApp > 0 && <span data-testid={`month-appt-count-${ds}`} className="text-[10px] px-1.5 py-0.5 rounded-full bg-brand text-white">{nApp}</span>}
                  {nPriv > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: TODO_COLORS[4].value }}>{nPriv}</span>}
                  {nTodo > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: TODO_COLORS[1].value }}>{nTodo}</span>}
                </div>
                {av.open && (
                  <div className="mt-1 h-1 rounded-full bg-[#EFEAE2] overflow-hidden" title={`Belasting ${mLoad}/${mMax}`} data-testid={`month-load-${ds}`}>
                    <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.round((mLoad / mMax) * 100))}%`, backgroundColor: mLoad > mMax ? "#C2607A" : mLoad >= mMax * 0.75 ? "#E0BE73" : "#B78A6E" }} />
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-ink-muted" data-testid="month-legend">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-brand" /> Afspraken</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: TODO_COLORS[4].value }} /> Privé</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: TODO_COLORS[1].value }} /> To-do's</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#FBEBD2] border border-[#EBD9B4]" /> Aardig druk (3+)</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#F6D89B] border border-[#E0BE73]" /> Vol (5+)</span>
          <span className="inline-flex items-center gap-1.5"><AlertTriangle className="w-3 h-3 text-[#C2607A]" /> Overvol (boven max. belasting)</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-5 h-1.5 rounded-full" style={{ background: "linear-gradient(90deg,#B78A6E,#E0BE73,#C2607A)" }} /> Belasting/dag (balkje)</span>
          <span className="inline-flex items-center gap-1.5"><Lock className="w-3 h-3" /> Gesloten dag</span>
          <span className="basis-full text-[11px] text-ink-muted/80" data-testid="month-load-note">Losse dag-to-do's tellen ook mee in de dagbelasting — daardoor kan een rustig ogende dag toch "vol" staan.</span>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-3xl font-medium tracking-tight">Agenda</h1>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-full border border-[#E8E2D9] bg-white p-0.5" data-testid="agenda-view-mode">
            {[["day", "Dag"], ["week", "Week"], ["month", "Maand"]].map(([val, label]) => (
              <button key={val} onClick={() => setView(val)} data-testid={`view-${val}`}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${view === val ? "bg-brand text-white" : "text-ink-muted hover:bg-cream-subtle"}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-full border border-[#E8E2D9] bg-white p-0.5" data-testid="agenda-view-filter">
            {[["all", "Alles"], ["treatments", "Behandelingen"], ["private", "Privé"]].map(([val, label]) => (
              <button key={val} onClick={() => setViewFilter(val)} data-testid={`filter-${val}`}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${viewFilter === val ? "bg-brand text-white" : "text-ink-muted hover:bg-cream-subtle"}`}>
                {label}
              </button>
            ))}
          </div>
          {view !== "month" && (
            <button onClick={() => setSelectMode((v) => !v)} data-testid="toggle-select-mode"
              className={`lg:hidden inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${selectMode ? "bg-brand text-white border-brand" : "bg-white text-ink-muted border-[#E8E2D9]"}`}>
              <MousePointerClick className="w-4 h-4" /> {selectMode ? "Selecteren aan" : "Selecteren"}
            </button>
          )}
          {isPlanner && (
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="planner-help-btn" title="Hoe werkt de agenda?"
                  className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-[#E8E2D9] bg-white text-ink-muted hover:text-brand transition-colors">
                  <InfoIcon className="w-4 h-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 rounded-2xl text-sm" data-testid="planner-help-popover">
                <div className="font-medium mb-2 flex items-center gap-1.5"><InfoIcon className="w-4 h-4 text-brand" /> Zo werkt de agenda</div>
                <ul className="space-y-2 text-ink-muted">
                  <li className="flex gap-2"><span className="text-brand">•</span> <span><strong>Dubbeltik</strong> op een leeg tijdvak voor een nieuwe afspraak, blokkade of privé.</span></li>
                  <li className="flex gap-2"><span className="text-brand">•</span> <span>Tik op een <strong>behandeling</strong> (linkerkolom) en daarna op een tijdvak om die in te plannen.</span></li>
                  <li className="flex gap-2"><span className="text-brand">•</span> <span><strong>Scroll</strong> vrij met je vinger. Houd een tijdvak <strong>ingedrukt</strong> — of zet <strong>Selecteren</strong> aan — om een periode te kiezen.</span></li>
                  <li className="flex gap-2"><span className="text-brand">•</span> <span><strong>Sleep</strong> een afspraak naar een ander tijdvak om te verzetten.</span></li>
                </ul>
              </PopoverContent>
            </Popover>
          )}
          <button onClick={() => { setAssistOpen(true); setAssistResults(null); }} data-testid="planner-assistant-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border border-brand/30 bg-brand-light text-brand-hover hover:bg-brand hover:text-white transition-colors">
            <Sparkles className="w-4 h-4" /> Assistent
          </button>
          <Button variant="outline" className="rounded-full text-sm" onClick={shareAgenda} data-testid="share-agenda-btn"><Share2 className="w-4 h-4 mr-1" /> Deel</Button>
          <Button size="icon" variant="outline" className="rounded-full" onClick={goPrev} data-testid="week-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" className="rounded-full text-sm" onClick={goToday} data-testid="week-today">Vandaag</Button>
          <Button size="icon" variant="outline" className="rounded-full" onClick={goNext} data-testid="week-next"><ChevronRight className="w-4 h-4" /></Button>
          <span className="text-sm text-ink-muted ml-2 capitalize">{navLabel}</span>
        </div>
      </div>

      {armedRes && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-2xl bg-brand-light border border-brand/30 px-4 py-3" data-testid="armed-reservation-banner">
          <div className="text-sm text-ink">
            <strong>Aanvraag inplannen:</strong> {armedRes.name}. Tik op een vrij tijdvak (ook op een gesloten dag) om de afspraak neer te zetten.
          </div>
          <Button size="sm" variant="ghost" onClick={() => setArmedRes(null)} data-testid="cancel-armed-res" className="rounded-full text-xs">Annuleren</Button>
        </div>
      )}

      {view !== "month" && (
        <div className="lg:hidden mb-3 flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-xs border" data-testid="select-mode-banner"
          style={{ backgroundColor: selectMode ? "var(--brand-light, #F5E9E4)" : "#FAF7F2", borderColor: selectMode ? "rgba(180,120,110,0.3)" : "#F2EBE1" }}>
          {selectMode ? (
            <>
              <span className="text-brand-hover">Selectiemodus <strong>aan</strong> — sleep over de agenda om een tijdvak te kiezen.</span>
              <button onClick={() => setSelectMode(false)} data-testid="select-mode-stop" className="text-ink-muted shrink-0 flex items-center gap-1"><X className="w-3.5 h-3.5" /> Stop</button>
            </>
          ) : (
            <span className="text-ink-muted">{isPlanner ? "Dubbeltik op een tijdvak voor een nieuwe afspraak. " : ""}Scroll vrij met je vinger; houd een tijdvak <strong>ingedrukt</strong> of zet <strong>Selecteren</strong> aan om een periode te kiezen.</span>
          )}
        </div>
      )}
      <button onClick={() => setLegendOpen((v) => !v)} data-testid="legend-toggle"
        className="lg:hidden mb-2 text-xs text-ink-muted underline underline-offset-2">
        {legendOpen ? "Legenda verbergen" : "Legenda tonen"}
      </button>
      <div className={`mb-4 flex-wrap items-center gap-x-4 gap-y-2 text-xs text-ink-muted ${legendOpen ? "flex" : "hidden"} lg:flex`} data-testid="agenda-legend">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-brand" /> Behandeling</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border-2 border-dashed border-[#3E9E6C]" style={{ backgroundImage: "repeating-linear-gradient(45deg,#9BD3B0 0,#9BD3B0 3px,#DCF0E4 3px,#DCF0E4 6px)" }} /> Aanbod geclaimd</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border-2 border-dashed border-[#D98A2B]" style={{ backgroundImage: "repeating-linear-gradient(45deg,#F6D89B 0,#F6D89B 3px,#FBEBD2 3px,#FBEBD2 6px)" }} /> Voorstel / verzet</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border border-[#DAD0C4]" style={{ backgroundImage: "repeating-linear-gradient(45deg,#E7DFD6 0,#E7DFD6 3px,#F1EBE3 3px,#F1EBE3 6px)" }} /> Geblokkeerd</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ backgroundColor: TODO_COLORS[4].value }} /> Privé afspraak</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ backgroundColor: TODO_COLORS[1].value }} /> To-do</span>
        <span className="inline-flex items-center gap-1.5" data-testid="legend-rest"><span className="w-3 h-3 rounded-sm border border-[#C9BFDD]/50" style={{ backgroundImage: "repeating-linear-gradient(45deg,#D9D2E6 0,#D9D2E6 3px,#ECE7F3 3px,#ECE7F3 6px)" }} /> Rusttijd</span>
        {markExt && (startHour < CORE_START || endHour > CORE_END) && (
          <span className="inline-flex items-center gap-1.5" data-testid="legend-extended"><span className="w-3 h-3 rounded-sm border border-[#D8D3EC]" style={{ backgroundColor: extColor }} /> Buiten werktijd</span>
        )}
        <span className="basis-full text-[11px] text-ink-muted/80" data-testid="legend-load-note">Tip: ook losse dag-to-do's (op de "Hele dag"-strook) tellen mee in de dagbelasting.</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr_210px] gap-6">
        {/* Treatments sidebar */}
        <aside className="bg-white rounded-2xl border border-[#E8E2D9] p-4 shadow-[0_8px_30px_rgb(74,63,63,0.04)] h-fit lg:sticky lg:top-6 order-2 lg:order-1">
          <h2 className="font-medium mb-1">Behandelingen</h2>
          <p className="text-xs text-ink-muted mb-4">Tik op een behandeling en daarna op een vrij tijdvak om in te plannen (of sleep op desktop). Tik op een leeg tijdvak voor een afspraak of blokkering.</p>
          {armed && (
            <div className="mb-3 flex items-center justify-between gap-2 rounded-xl bg-brand-light border border-brand/30 px-3 py-2 text-xs" data-testid="armed-banner">
              <span className="text-brand-hover leading-tight">Tik nu op een vrij tijdvak</span>
              <button onClick={() => setArmed(null)} data-testid="cancel-armed" className="text-ink-muted hover:text-brand shrink-0 flex items-center gap-1"><X className="w-3.5 h-3.5" /> Stop</button>
            </div>
          )}
          <div className="space-y-4 max-h-[60vh] overflow-auto no-scrollbar">
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                <div className="text-xs uppercase tracking-wide text-brand mb-1.5">{cat}</div>
                <div className="space-y-1.5">
                  {items.map((t) => (
                    <div key={t.id} draggable data-testid={`drag-treatment-${t.id}`}
                      onDragStart={() => setDrag({ type: "treatment", id: t.id })}
                      onDragEnd={() => { setDrag(null); setDragOver(null); }}
                      onClick={() => setArmed(armed === t.id ? null : t.id)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl cursor-pointer active:cursor-grabbing transition-colors group ${
                        armed === t.id ? "bg-brand-light ring-2 ring-inset ring-brand" : "bg-cream-subtle/60 hover:bg-brand-light"}`}>
                      <GripVertical className="w-3.5 h-3.5 text-ink-muted group-hover:text-brand" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm truncate">{t.name}</div>
                        <div className="text-[11px] text-ink-muted flex items-center gap-1"><Clock className="w-3 h-3" />{t.duration_min} min</div>
                      </div>
                      {armed === t.id && <Check className="w-4 h-4 text-brand shrink-0" />}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* Calendar grid */}
        <div ref={gridRef} className="bg-white rounded-2xl border border-[#E8E2D9] shadow-[0_8px_30px_rgb(74,63,63,0.04)] overflow-hidden order-1 lg:order-2">
          {view === "month" ? (
            renderMonth()
          ) : view === "day" ? (
            <div>
              {/* mobile day switcher */}
              <div className="flex items-center justify-between px-3 py-3 border-b border-[#F2EBE1] gap-2">
                <Button size="icon" variant="outline" className="rounded-full shrink-0" data-testid="mday-prev"
                  onClick={() => { if (mobileIdx > 0) setMobileIdx(mobileIdx - 1); else { setWeekStart(addWeeks(weekStart, -1)); setMobileIdx(6); } }}><ChevronLeft className="w-4 h-4" /></Button>
                <div className="text-center min-w-0">
                  <div className="text-xs text-ink-muted capitalize">{format(days[mobileIdx], "EEEE", { locale: nl })}</div>
                  <div className={`font-display text-lg capitalize ${isSameDay(days[mobileIdx], new Date()) ? "text-brand" : ""}`}>{format(days[mobileIdx], "d MMMM", { locale: nl })}</div>
                  {!dayAvail(days[mobileIdx]).open && <div className="text-[10px] text-ink-muted">Gesloten</div>}
                  {dayAvail(days[mobileIdx]).open && <div className="max-w-[160px] mx-auto"><LoadBar date={days[mobileIdx]} /></div>}
                  {(() => { const mm = dayMeta[format(days[mobileIdx], "yyyy-MM-dd")]; return mm && (mm.summary || mm.manual_load) ? (
                    <div className="mt-1 text-[11px] flex items-center justify-center gap-1.5 max-w-[240px] mx-auto" data-testid="mday-summary">
                      {mm.manual_load ? <span className="inline-flex items-center gap-0.5 text-brand font-medium shrink-0">+{mm.manual_load}</span> : null}
                      {mm.summary ? <span className="truncate italic text-ink-muted" title={mm.summary}>"{mm.summary}"</span> : null}
                    </div>) : null; })()}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {dayAvail(days[mobileIdx]).open && (
                    <Button size="icon" variant="outline" className="rounded-full" data-testid="mday-block" title="Dag(deel) blokkeren"
                      onClick={() => openBlockWholeDay(days[mobileIdx])}><Lock className="w-4 h-4" /></Button>
                  )}
                  <Button size="icon" variant="outline" className="rounded-full" data-testid="mday-note" title="Dagnotitie / belasting"
                    onClick={() => setDayEdit(days[mobileIdx])}><StickyNote className={`w-4 h-4 ${dayMeta[format(days[mobileIdx], "yyyy-MM-dd")]?.summary ? "text-brand" : ""}`} /></Button>
                  <Button size="icon" variant="outline" className="rounded-full" data-testid="mday-next"
                    onClick={() => { if (mobileIdx < 6) setMobileIdx(mobileIdx + 1); else { setWeekStart(addWeeks(weekStart, 1)); setMobileIdx(0); } }}><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
              {/* Hele dag: dag-to-do's (zonder tijdblok) */}
              {(() => {
                const day = days[mobileIdx];
                const ds = format(day, "yyyy-MM-dd");
                const dts = dayTodos(day);
                const over = drag?.type === "todo" && allDayOver === ds;
                return (
                  <div data-testid={`allday-${ds}`}
                    onDragOver={(e) => { if (drag?.type === "todo") { e.preventDefault(); setAllDayOver(ds); } }}
                    onDragLeave={() => setAllDayOver((v) => (v === ds ? null : v))}
                    onDrop={() => assignTodoToDay(day)}
                    className={`flex items-center gap-1.5 flex-wrap px-3 py-1.5 border-b border-[#F2EBE1] transition-colors ${over ? "bg-brand-light" : ""}`}>
                    <span className="text-[10px] text-ink-muted shrink-0">Hele dag:</span>
                    {dts.length === 0 && <span className="text-[10px] text-ink-muted/70">sleep hier een to-do naartoe</span>}
                    {dts.map(renderAllDayChip)}
                  </div>
                );
              })()}
              <div className="grid" style={{ gridTemplateColumns: `56px 1fr` }}>
                <div>
                  {Array.from({ length: SLOTS }).map((_, i) => (
                    <div key={i} style={{ height: SLOT_H }} className="text-[11px] text-ink-muted text-right pr-2 pt-1 border-r border-[#F2EBE1]">
                      {i % 2 === 0 ? slotLabel(i) : ""}
                    </div>
                  ))}
                </div>
                {renderDayColumn(days[mobileIdx], mobileIdx)}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="min-w-[840px] lg:min-w-0">
                {(() => {
                  const wl = weekDays.reduce((sm, d) => sm + dayLoadFor(d), 0);
                  const wmax = weekDays.reduce((sm, d) => sm + (dayAvail(d).open ? maxForDay(d) : 0), 0);
                  return (
                    <div className="flex items-center gap-2 px-2 py-2 text-xs border-b border-[#F2EBE1]" data-testid="week-load-total">
                      <span className="text-ink-muted">Belasting deze week</span>
                      <div className="flex-1 h-1.5 rounded-full bg-[#EFEAE2] overflow-hidden max-w-[220px]"><div className="h-full rounded-full" style={{ width: `${wmax ? Math.min(100, Math.round((wl / wmax) * 100)) : 0}%`, backgroundColor: wl > wmax ? "#C2607A" : wl >= wmax * 0.75 ? "#E0BE73" : "#B78A6E" }} /></div>
                      <span className="font-medium tabular-nums text-ink">{wl}{wmax ? ` / ${wmax}` : ""}</span>
                    </div>
                  );
                })()}
                {/* Day headers */}
                <div className="grid" style={{ gridTemplateColumns: `64px repeat(${weekDays.length}, 1fr)` }}>
                  <div className="border-b border-r border-[#F2EBE1]" />
                  {weekDays.map((d) => {
                    const today = isSameDay(d, new Date());
                    const av = dayAvail(d);
                    return (
                      <div key={d.toISOString()} className={`relative text-center py-3 border-b border-[#F2EBE1] group ${today ? "bg-brand-light/50" : !av.open ? "bg-[#F7F3EE]" : ""}`}>
                        <div className="text-xs text-ink-muted capitalize">{format(d, "EEE", { locale: nl })}</div>
                        <div className={`text-lg font-display ${today ? "text-brand" : ""}`}>{format(d, "d")}</div>
                        {!av.open && <div className="text-[10px] text-ink-muted">Gesloten</div>}
                        {av.open && <LoadBar date={d} />}
                        {av.open && (
                          <button data-testid={`block-day-${format(d, "yyyy-MM-dd")}`} onClick={() => openBlockWholeDay(d)}
                            title="Dag(deel) blokkeren"
                            className="absolute top-1.5 right-1.5 p-1 rounded-full text-ink-muted bg-white/70 lg:bg-transparent opacity-100 lg:opacity-0 lg:group-hover:opacity-100 hover:bg-white/70 hover:text-brand transition-opacity">
                            <Lock className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {(
                          <button data-testid={`day-note-${format(d, "yyyy-MM-dd")}`} onClick={() => setDayEdit(d)}
                            title={dayMeta[format(d, "yyyy-MM-dd")]?.summary || "Dagnotitie / belasting"}
                            className={`absolute top-1.5 left-1.5 p-1 rounded-full bg-white/70 lg:bg-transparent hover:bg-white/70 hover:text-brand transition-opacity ${dayMeta[format(d, "yyyy-MM-dd")]?.summary ? "text-brand opacity-100" : "text-ink-muted opacity-100 lg:opacity-0 lg:group-hover:opacity-100"}`}>
                            <StickyNote className={`w-3.5 h-3.5 ${dayMeta[format(d, "yyyy-MM-dd")]?.summary ? "fill-brand/20" : ""}`} />
                          </button>
                        )}
                        {dayMeta[format(d, "yyyy-MM-dd")]?.summary && (
                          <button type="button" onClick={() => setDayEdit(d)} data-testid={`day-summary-week-${format(d, "yyyy-MM-dd")}`}
                            title={dayMeta[format(d, "yyyy-MM-dd")].summary}
                            className="block w-full px-1.5 mt-1 text-[10px] leading-snug text-ink-muted italic line-clamp-2 hover:text-brand transition-colors text-center">
                            "{dayMeta[format(d, "yyyy-MM-dd")].summary}"
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* Hele dag: dag-to-do's (zonder tijdblok) */}
                <div className="grid border-b border-[#F2EBE1]" style={{ gridTemplateColumns: `64px repeat(${weekDays.length}, 1fr)` }}>
                  <div className="text-[10px] text-ink-muted text-right pr-2 py-1 border-r border-[#F2EBE1] flex items-center justify-end">Hele dag</div>
                  {weekDays.map((day, di) => {
                    const dts = dayTodos(day);
                    const over = drag?.type === "todo" && allDayOver === format(day, "yyyy-MM-dd");
                    return (
                      <div key={di} data-testid={`allday-${format(day, "yyyy-MM-dd")}`}
                        onDragOver={(e) => { if (drag?.type === "todo") { e.preventDefault(); setAllDayOver(format(day, "yyyy-MM-dd")); } }}
                        onDragLeave={() => setAllDayOver((v) => (v === format(day, "yyyy-MM-dd") ? null : v))}
                        onDrop={() => assignTodoToDay(day)}
                        className={`min-h-[26px] border-r border-[#F2EBE1] last:border-r-0 p-1 space-y-1 overflow-hidden transition-colors ${over ? "bg-brand-light ring-1 ring-inset ring-brand/40" : ""}`}>
                        {dts.map(renderAllDayChip)}
                      </div>
                    );
                  })}
                </div>
                {/* Body */}
                <div className="grid" style={{ gridTemplateColumns: `64px repeat(${weekDays.length}, 1fr)` }}>
                  <div>
                    {Array.from({ length: SLOTS }).map((_, i) => (
                      <div key={i} style={{ height: SLOT_H }} className="text-[11px] text-ink-muted text-right pr-2 pt-1 border-r border-[#F2EBE1]">
                        {i % 2 === 0 ? slotLabel(i) : ""}
                      </div>
                    ))}
                  </div>
                  {weekDays.map((day, di) => renderDayColumn(day, di))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* To-Do panel (right) */}
        <aside className="bg-white rounded-2xl border border-[#E8E2D9] p-4 shadow-[0_8px_30px_rgb(74,63,63,0.04)] h-fit lg:sticky lg:top-6 order-3">
          <h2 className="font-medium mb-1 flex items-center gap-1.5"><CheckSquare className="w-4 h-4 text-brand" strokeWidth={1.7} /> To-Do's</h2>
          <p className="text-xs text-ink-muted mb-3">Sleep een to-do op een tijdvak om er tijd voor te blokken (blokkeert ook online reserveren), of op de <b>Hele dag</b>-strook om hem aan een dag te koppelen zonder vaste tijd.</p>
          <div className="space-y-1.5 max-h-[60vh] overflow-auto no-scrollbar">
            {openTodos.length === 0 && <div className="text-xs text-ink-muted py-2">Geen open to-do's.</div>}
            {openTodos.map((t) => (
              <div key={t.id} draggable data-testid={`drag-todo-${t.id}`}
                onDragStart={() => setDrag({ type: "todo", id: t.id })}
                onDragEnd={() => { setDrag(null); setDragOver(null); }}
                className="flex items-center gap-2 px-2.5 py-2 rounded-xl cursor-pointer active:cursor-grabbing bg-cream-subtle/60 hover:bg-brand-light group transition-colors">
                <GripVertical className="w-3.5 h-3.5 text-ink-muted group-hover:text-brand shrink-0" />
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: t.color }} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{t.title}</div>
                  <div className="text-[11px] text-ink-muted flex items-center gap-1 flex-wrap"><Clock className="w-3 h-3" />{t.duration_min} min{(t.subtasks?.filter((x) => !x.done).length || 0) > 0 && <span className="inline-flex items-center gap-0.5"><ListChecks className="w-3 h-3" /> {t.subtasks.filter((x) => !x.done).length} open</span>}{t.scheduled_start ? " · op agenda" : ""}</div>
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => navigate("/admin/todos")} data-testid="manage-todos-link" className="mt-3 text-xs text-brand hover:underline">Alle to-do's beheren →</button>
        </aside>
      </div>

      {/* Day detail (from month view) */}
      {dayEdit && (
        <DayEditDialog date={dayEdit} meta={dayMeta[format(dayEdit, "yyyy-MM-dd")]}
          onClose={() => setDayEdit(null)} onSaved={() => { setDayEdit(null); loadDayMeta(); }} />
      )}
      <Dialog open={!!dayDetail} onOpenChange={(o) => !o && setDayDetail(null)}>
        <DialogContent className="rounded-2xl max-w-sm" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display capitalize">{dayDetail ? format(dayDetail, "EEEE d MMMM yyyy", { locale: nl }) : ""}</DialogTitle></DialogHeader>
          {dayDetail && ddOpen && (() => {
            const ld = ddAppts.reduce((sm, a) => sm + loadFromDur(a.duration_min || 30), 0)
              + ddPriv.reduce((sm, b) => { try { const [h1, m1] = b.start.split(":").map(Number); const [h2, m2] = b.end.split(":").map(Number); const dl = loadFromDur((h2 * 60 + m2) - (h1 * 60 + m1)); return sm + (b.load ? b.load : dl); } catch { return sm; } }, 0)
              + ddTodos.filter((t) => !t.scheduled_start).reduce((sm, t) => sm + ((t.load || 0) || loadFromDur(t.duration_min || 30)), 0);
            const mx = maxForDay(dayDetail);
            const heaviest = ddAppts.slice().sort((a, b) => (b.duration_min || 0) - (a.duration_min || 0))[0];
            const col = ld > mx ? "#C2607A" : ld >= mx * 0.75 ? "#E0BE73" : "#B78A6E";
            return (
              <div className="rounded-xl bg-cream-subtle/60 px-3 py-2.5" data-testid="daydetail-load">
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="text-ink-muted">Belasting van deze dag</span>
                  <span className="font-medium tabular-nums">{ld} / {mx}</span>
                </div>
                <div className="h-1.5 rounded-full bg-[#EFEAE2] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.round((ld / mx) * 100))}%`, backgroundColor: col }} /></div>
                <p className="text-[11px] text-ink-muted mt-1.5">
                  {ld > mx ? "Boven je maximum — druk." : ld >= mx * 0.75 ? "Aardig gevuld." : ld === 0 ? "Nog helemaal vrij." : "Rustige dag."}
                  {heaviest ? ` Zwaarste: ${heaviest.treatment_name || "behandeling"} (${heaviest.duration_min} min).` : ""}
                </p>
              </div>
            );
          })()}
          <div className="space-y-4 max-h-[50vh] overflow-y-auto">
            {!ddOpen && <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">Deze dag is normaal gesloten.</p>}
            <div>
              <div className="text-xs uppercase tracking-wide text-ink-muted mb-1.5">Afspraken</div>
              {ddAppts.length === 0 && ddPriv.length === 0 ? (
                <p className="text-sm text-ink-muted">Geen afspraken op deze dag.</p>
              ) : (
                <div className="space-y-1.5">
                  {ddAppts.map((a) => (
                    <div key={a.id} className="flex items-center gap-2 text-sm" data-testid={`daydetail-appt-${a.id}`}>
                      <span className="tabular-nums text-ink-muted w-11 shrink-0">{(() => { try { return format(parseISO(a.start), "HH:mm"); } catch { return ""; } })()}</span>
                      <span className="w-1.5 h-1.5 rounded-full bg-brand shrink-0" />
                      <span className="min-w-0 truncate"><strong>{a.customer_name}</strong> · {a.treatment_name}</span>
                    </div>
                  ))}
                  {ddPriv.map((b) => (
                    <div key={b.id} className="flex items-center gap-2 text-sm">
                      <span className="tabular-nums text-ink-muted w-11 shrink-0">{b.start || ""}</span>
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: b.color || TODO_COLORS[4].value }} />
                      <span className="min-w-0 truncate">{b.title || "Privé"} · Privé</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {ddTodos.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wide text-ink-muted mb-1.5">To-do's</div>
                <ul className="text-sm list-disc pl-5 space-y-0.5 text-ink">{ddTodos.map((t) => <li key={t.id}>{t.title}</li>)}</ul>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:flex-wrap">
            <Button variant="outline" onClick={() => { const d = dayDetail; setDayDetail(null); openApptForDay(d); }} data-testid="daydetail-new-appt" className="rounded-full border-[#E8E2D9]"><CalendarPlus className="w-4 h-4 mr-1" /> Afspraak</Button>
            <Button variant="outline" onClick={() => { const d = dayDetail; setDayDetail(null); openBlockForDay(d); }} data-testid="daydetail-new-block" className="rounded-full border-[#E8E2D9]"><Ban className="w-4 h-4 mr-1" /> Blokkade</Button>
            <Button variant="outline" onClick={() => { setDayDetail(null); setAssistOpen(true); setAssistResults(null); }} data-testid="daydetail-assistant" className="rounded-full border-brand/30 bg-brand-light text-brand-hover"><Sparkles className="w-4 h-4 mr-1" /> Assistent</Button>
            {!ddOpen && <Button variant="outline" onClick={() => openClosedDay(dayDetail)} data-testid="confirm-open-day" className="rounded-full border-[#E8E2D9]">Dag eenmalig openen</Button>}
            <Button onClick={() => { const d = dayDetail; setDayDetail(null); goToDay(d); }} data-testid="daydetail-open-day" className="rounded-full bg-brand hover:bg-brand-hover text-white">Open dagweergave</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign modal */}
      <Dialog open={assistOpen} onOpenChange={(o) => { setAssistOpen(o); if (!o) setAssistCustomerId(""); }}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-brand" /> Planningsassistent
              <button type="button" data-testid="assist-help-btn" onClick={() => setAssistHelp((v) => !v)} className="ml-auto text-ink-muted hover:text-brand"><InfoIcon className="w-4 h-4" /></button>
            </DialogTitle>
          </DialogHeader>
          {assistCustomerId && customers.find((c) => c.id === assistCustomerId) && (
            <div data-testid="assist-customer" className="flex items-center gap-2 rounded-xl bg-brand-light text-brand-hover text-sm px-3 py-2 -mt-1">
              <UserRound className="w-4 h-4" /> Voor: <strong>{customers.find((c) => c.id === assistCustomerId).name}</strong>
            </div>
          )}
          {assistHelp && (
            <div data-testid="assist-help" className="rounded-xl bg-cream-subtle/60 text-xs text-ink-muted p-3 space-y-1.5 -mt-1">
              <p><strong>Hoe werkt het?</strong> Elke behandeling heeft een <strong>belasting</strong> (1–5; een massage weegt zwaarder dan gellak). Per dag telt de assistent die belasting op.</p>
              <p>Hij zoekt vrije tijd binnen je openingstijden (rekening houdend met afspraken, blokkades en marge rond privé) en rangschikt de dagen: <span className="text-[#3B7A57] font-medium">Ideaal</span> = binnen je ideale bereik, <span className="text-[#3C6E8F] font-medium">Rustig</span> = eronder, <span className="text-[#8A6A2F] font-medium">Drukker</span> = erboven. Dagen boven je max. belasting worden overgeslagen.</p>
              <p>Pas je grenzen aan in <strong>Instellingen</strong> (max. per dag, ideaal bereik, per weekdag, marge rond privé).</p>
            </div>
          )}
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-ink-muted mb-1.5 block">Type afspraak</Label>
              <div className="flex gap-2">
                {[["treatment", "Behandeling"], ["private", "Privé"], ["todo", "To-do"]].map(([v, l]) => (
                  <button key={v} data-testid={`assist-type-${v}`} onClick={() => { setAssist({ ...assist, type: v }); setAssistResults(null); }}
                    className={`flex-1 rounded-full py-2 text-sm border transition-colors ${assist.type === v ? "bg-brand text-white border-brand" : "bg-white text-ink-muted border-[#E8E2D9]"}`}>{l}</button>
                ))}
              </div>
            </div>
            {assist.type === "treatment" ? (
              <div>
                <Label className="text-sm text-ink-muted mb-1.5 block">Behandeling</Label>
                <Select value={assist.treatment_id} onValueChange={(v) => { const t = treatments.find((x) => x.id === v); const dl = t ? ((t.load || 0) || loadFromDur(t.duration_min || 30)) : 2; setAssist({ ...assist, treatment_id: v, load: dl }); setAssistResults(null); }}>
                  <SelectTrigger className="rounded-xl border-[#E8E2D9]" data-testid="assist-treatment"><SelectValue placeholder="Kies een behandeling" /></SelectTrigger>
                  <SelectContent>{treatments.filter((t) => t.active !== false).map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.duration_min} min</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <div><Label className="text-sm text-ink-muted mb-1.5 block">Duur (min)</Label><Input type="number" data-testid="assist-duration" value={assist.duration_min} onChange={(e) => { setAssist({ ...assist, duration_min: e.target.value }); setAssistResults(null); }} className="rounded-xl border-[#E8E2D9]" /></div>
            )}
            <div>
              <Label className="text-sm text-ink-muted mb-1.5 block">Belasting van deze afspraak (telt mee in de dagtotaal)</Label>
              <div className="flex gap-1.5">
                {[1, 2, 3, 4, 5].map((v) => (
                  <button key={v} data-testid={`assist-load-${v}`} onClick={() => setAssist({ ...assist, load: v })}
                    className={`flex-1 rounded-lg py-2 text-sm border transition-colors ${Number(assist.load) === v ? "bg-brand text-white border-brand" : "bg-white text-ink-muted border-[#E8E2D9]"}`}>{v}</button>
                ))}
              </div>
              <p className="text-[11px] text-ink-muted mt-1">1 = licht · 3 = normaal · 5 = zwaar{assist.type === "treatment" ? " (standaard uit de behandeling; je kunt hem aanpassen)" : ""}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm text-ink-muted mb-1.5 block">Wanneer</Label>
                <div className="flex flex-col gap-1">{[["this_week", "Deze week"], ["next_week", "Volgende week"], ["within_days", "Binnen X dagen"]].map(([v, l]) => (<button key={v} data-testid={`assist-when-${v}`} onClick={() => { setAssist({ ...assist, when: v }); setAssistResults(null); }} className={`rounded-lg py-1.5 text-sm border ${assist.when === v ? "bg-brand-light border-brand text-brand-hover" : "bg-white text-ink-muted border-[#E8E2D9]"}`}>{l}</button>))}</div>
                {assist.when === "within_days" && <Input type="number" data-testid="assist-within-days" value={assist.within_days} onChange={(e) => setAssist({ ...assist, within_days: e.target.value })} className="mt-2 rounded-xl border-[#E8E2D9]" />}
              </div>
              <div><Label className="text-sm text-ink-muted mb-1.5 block">Dagdeel</Label>
                <div className="flex flex-col gap-1">{[["any", "Maakt niet uit"], ["morning", "Ochtend"], ["afternoon", "Middag"]].map(([v, l]) => (<button key={v} onClick={() => { setAssist({ ...assist, daypart: v }); setAssistResults(null); }} className={`rounded-lg py-1.5 text-sm border ${assist.daypart === v ? "bg-brand-light border-brand text-brand-hover" : "bg-white text-ink-muted border-[#E8E2D9]"}`}>{l}</button>))}</div>
              </div>
            </div>
            <Button onClick={runAssist} disabled={assistLoading || (assist.type === "treatment" && !assist.treatment_id)} data-testid="assist-run" className="w-full rounded-full bg-brand hover:bg-brand-hover text-white">{assistLoading ? "Zoeken…" : "Zoek beste momenten"}</Button>
            {assistResults && (
              <div className="space-y-2 max-h-64 overflow-y-auto pt-1" data-testid="assist-results">
                {assistResults.suggestions.length === 0 && <p className="text-sm text-ink-muted text-center py-4">Geen passend moment binnen je belastingsgrens ({assistResults.max_load}). Probeer een andere periode of dagdeel.</p>}
                {assistResults.suggestions.length > 0 && (() => {
                  const byDate = {};
                  assistResults.suggestions.forEach((s) => { if (!byDate[s.date]) byDate[s.date] = s; });
                  const days = Object.values(byDate);
                  const rustig = days.filter((s) => !s.over_capacity && !s.within_ideal && s.day_load_after < s.ideal_min).length;
                  const ideaal = days.filter((s) => !s.over_capacity && s.within_ideal).length;
                  const drukker = days.filter((s) => !s.over_capacity && !s.within_ideal && s.day_load_after >= s.ideal_min).length;
                  const vol = days.filter((s) => s.over_capacity).length;
                  const parts = [];
                  if (rustig) parts.push(`${rustig} rustige`);
                  if (ideaal) parts.push(`${ideaal} ideale`);
                  if (drukker) parts.push(`${drukker} drukke`);
                  if (vol) parts.push(`${vol} volle`);
                  return <div data-testid="assist-summary" className="text-xs text-ink-muted bg-cream-subtle/60 rounded-lg px-3 py-1.5">{parts.join(" · ")} {days.length === 1 ? "dag" : "dagen"} gevonden</div>;
                })()}
                {assistResults.suggestions.map((s, idx) => (
                  <button key={idx} data-testid={`assist-suggestion-${idx}`} onClick={() => applySuggestion(s)} className={`w-full text-left rounded-xl border p-3 transition-colors ${s.over_capacity ? "border-[#E7B9C2] bg-[#FBF1F3] hover:border-[#C2607A]" : "border-[#E8E2D9] hover:border-brand hover:bg-brand-light/30"}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-medium capitalize">{s.date_label} · {s.start}</div>
                      {s.over_capacity ? <span className="text-xs px-2 py-0.5 rounded-full bg-[#F6DCDF] text-[#C2607A] inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Vol</span>
                        : s.within_ideal ? <span className="text-xs px-2 py-0.5 rounded-full bg-[#E7F0E9] text-[#3B7A57]">Ideaal</span>
                        : s.day_load_after < s.ideal_min ? <span className="text-xs px-2 py-0.5 rounded-full bg-[#E6EFF5] text-[#3C6E8F]">Rustig</span>
                        : <span className="text-xs px-2 py-0.5 rounded-full bg-[#F3E7D3] text-[#8A6A2F]">Drukker</span>}
                    </div>
                    <div className="text-xs text-ink-muted mt-1">{s.reason}</div>
                    <div className="mt-2 h-1.5 rounded-full bg-[#EFEAE2] overflow-hidden"><div className="h-full" style={{ width: `${Math.min(100, Math.round((s.day_load_after / s.max_load) * 100))}%`, backgroundColor: s.over_capacity ? "#C2607A" : "#B78A6E" }} /></div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="rounded-2xl" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display">Afspraak inplannen</DialogTitle></DialogHeader>
          {modal && (() => {
            const selTreatment = treatments.find((t) => t.id === apptTreatmentId);
            return (
            <div className="space-y-4">
              <div className="rounded-xl bg-cream-subtle/60 p-4">
                <div className="text-sm capitalize">{format(modal.start, "EEEE d MMMM yyyy", { locale: nl })}</div>
                {selTreatment && <div className="text-sm text-ink-muted flex items-center gap-1 mt-1"><Clock className="w-3 h-3" />{selTreatment.duration_min} min · {selTreatment.rest_min || 0} min rust</div>}
              </div>
              {modal.closedDay && (
                <div data-testid="closed-day-warning" className="rounded-xl bg-[#FBEBD2] border border-dashed border-[#E0A24A] text-[#9A5B12] px-3 py-2.5 text-sm flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>Let op: dit is <strong>buiten de openingstijden</strong> (gesloten dag/tijd). Je kunt hier als beheer tóch een afspraak inplannen.</span>
                </div>
              )}
              <div>
                <Label className="text-sm text-ink-muted mb-1.5 block">Starttijd *</Label>
                <Input type="time" step="900" data-testid="assign-time"
                  value={format(modal.start, "HH:mm")}
                  onChange={(e) => {
                    const [h, m] = e.target.value.split(":").map(Number);
                    const d = new Date(modal.start); d.setHours(h || 0, m || 0, 0, 0);
                    setModal({ ...modal, start: d }); setWarnings([]);
                  }}
                  className="rounded-xl border-[#E8E2D9] w-36" />
              </div>
              <div>
                <Label className="text-sm text-ink-muted mb-1.5 block">Behandeling *</Label>
                <Select value={apptTreatmentId} onValueChange={(v) => { setApptTreatmentId(v); setWarnings([]); }}>
                  <SelectTrigger className="rounded-xl border-[#E8E2D9]" data-testid="assign-treatment"><SelectValue placeholder="Kies een behandeling" /></SelectTrigger>
                  <SelectContent>
                    {treatments.map((t) => (
                      <SelectItem key={t.id} value={t.id} data-testid={`assign-treatment-${t.id}`}>{t.name} · {t.duration_min} min</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-ink-muted mb-1.5 block">Klant koppelen *</Label>
                <CustomerPicker customers={customers} value={customerId}
                  onChange={setCustomerId} onCreated={loadCustomers} />
                {!customers.length && <p className="text-xs text-ink-muted mt-1">Nog geen klanten. Voeg eerst een klant toe onder "Klanten".</p>}
                {(() => {
                  const sc = customers.find((c) => c.id === customerId);
                  if (!sc || sc.email || sc.phone) return null;
                  return (
                    <div data-testid="appt-no-contact" className="mt-2 flex items-start gap-2 rounded-xl bg-[#F3E7D3]/60 text-[#8A6A2F] text-xs px-3 py-2">
                      <BellOff className="w-4 h-4 mt-0.5 shrink-0" /> Deze klant heeft geen e-mail of telefoon — er wordt geen bevestiging of herinnering verstuurd.
                    </div>
                  );
                })()}
              </div>
              <div>
                <Label className="text-sm text-ink-muted mb-1.5 block">Notities</Label>
                <Textarea data-testid="assign-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-xl border-[#E8E2D9]" rows={2} />
              </div>
              {warnings.length > 0 && (
                <div data-testid="rest-warning" className="rounded-xl bg-[#FBF0E6] border border-[#EAD6BE] p-3 text-sm">
                  <div className="flex items-center gap-1.5 font-medium text-[#9A6B2F] mb-1">
                    <AlertTriangle className="w-4 h-4" /> Let op
                  </div>
                  <ul className="list-disc pl-5 space-y-0.5 text-[#7A5A2E]">
                    {warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
            </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setModal(null)}>Annuleren</Button>
            <Button onClick={() => confirmAppt(warnings.length > 0)} data-testid="confirm-appt-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white">
              {warnings.length > 0 ? "Toch inplannen" : ((() => { const sc = customers.find((c) => c.id === customerId); return (sc && !sc.email && !sc.phone) ? "Inplannen" : "Bevestigen & e-mail"; })())}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail modal */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-2xl" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display">Afspraak</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              <Info label="Klant" value={detail.customer_name} />
              <Info label="Telefoon" value={detail.customer_phone} />
              <Info label="E-mail" value={detail.customer_email} />
              <Info label="Behandeling" value={detail.treatment_name} />
              <Info label="Wanneer" value={format(parseISO(detail.start), "EEEE d MMMM yyyy · HH:mm", { locale: nl })} />
              <Info label="Duur" value={`${detail.duration_min} min`} />
              <Info label="Status" value={detail.status === "cancelled" ? "Geannuleerd" : detail.status === "no_show" ? "Niet verschenen" : detail.status === "reschedule_pending" ? "Verzetten aangevraagd" : detail.status === "proposal_pending" ? "Voorstel · wacht op akkoord" : detail.status === "offer_pending" ? "Aanbod geclaimd · te bevestigen" : "Bevestigd"} />
              {detail.status === "offer_pending" && (
                <div className="rounded-xl bg-[#FBEBD2] border border-dashed border-[#E0A24A] text-[#9A5B12] px-3 py-2 text-sm" data-testid="detail-offer">
                  De klant heeft dit plekje via een aanbod geclaimd. Bevestig om de afspraak definitief te maken.
                  <Button size="sm" onClick={() => confirmOfferAppt(detail.id)} data-testid="confirm-offer-btn" className="mt-2 rounded-full bg-brand hover:bg-brand-hover text-white h-8 text-xs">Bevestigen &amp; klant informeren</Button>
                </div>
              )}
              {detail.status === "proposal_pending" && (
                <div className="rounded-xl bg-[#FBEBD2] border border-dashed border-[#E0A24A] text-[#9A5B12] px-3 py-2 text-sm" data-testid="detail-proposal">
                  Voorstel verstuurd naar de klant — wacht op akkoord (min. 24u vooraf).
                  {detail.proposal_comment && <div className="mt-1 italic">"{detail.proposal_comment}"</div>}
                </div>
              )}
              {detail.status === "reschedule_pending" && detail.proposed_start && (
                <div className="rounded-xl bg-[#FBEBD2] border border-dashed border-[#E0A24A] text-[#9A5B12] px-3 py-2 text-sm capitalize" data-testid="detail-proposed">
                  Voorgestelde nieuwe tijd: <strong>{format(parseISO(detail.proposed_start), "EEEE d MMMM · HH:mm", { locale: nl })}</strong> — wacht op bevestiging van de klant.
                </div>
              )}
              {detail.notes && <Info label="Notities" value={detail.notes} />}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => removeAppt(detail.id)} data-testid="delete-appt-btn">
              <Trash2 className="w-4 h-4 mr-1 text-destructive" /> Verwijderen
            </Button>
            <Button className="rounded-full bg-brand hover:bg-brand-hover text-white" onClick={() => setDetail(null)}>Sluiten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block modal */}
      <Dialog open={!!blockModal} onOpenChange={(o) => { if (!o) { setBlockModal(null); setBlockWarn([]); } }}>
        <DialogContent className="rounded-2xl" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Ban className="w-5 h-5 text-brand" /> {blockModal?.id ? "Blokkering bewerken" : "Tijd blokkeren"}</DialogTitle></DialogHeader>
          {blockModal && (
            <div className="space-y-4">
              <p className="text-sm text-ink-muted capitalize">
                {blockModal.dates && blockModal.dates.length > 1 ? `${blockModal.dates.length} dagen geselecteerd` : format(parseISO(`${blockModal.date}T00:00:00`), "EEEE d MMMM yyyy", { locale: nl })}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm text-ink-muted mb-1.5 block">Van</Label>
                  <Input type="time" step="900" data-testid="block-start" value={blockModal.start}
                    onChange={(e) => { setBlockModal({ ...blockModal, start: e.target.value }); setBlockWarn([]); }} className="rounded-xl border-[#E8E2D9]" />
                </div>
                <div>
                  <Label className="text-sm text-ink-muted mb-1.5 block">Tot</Label>
                  <Input type="time" step="900" data-testid="block-end" value={blockModal.end}
                    onChange={(e) => { setBlockModal({ ...blockModal, end: e.target.value }); setBlockWarn([]); }} className="rounded-xl border-[#E8E2D9]" />
                </div>
              </div>
              <div>
                <Label className="text-sm text-ink-muted mb-1.5 block">Notitie (optioneel)</Label>
                <Input data-testid="block-note" value={blockModal.note} placeholder="bijv. lunch, pauze, privé"
                  onChange={(e) => setBlockModal({ ...blockModal, note: e.target.value })} className="rounded-xl border-[#E8E2D9]" />
              </div>
              <div>
                <Label className="text-sm text-ink-muted mb-1.5 block">Belasting (optioneel)</Label>
                <div className="flex gap-1.5 flex-wrap">
                  <button type="button" data-testid="block-load-none" onClick={() => { setBlockModal({ ...blockModal, load: 0 }); setBlockWarn([]); }}
                    className={`h-9 px-3 rounded-lg border text-sm transition-colors ${!blockModal.load ? "bg-brand text-white border-brand" : "bg-white text-ink-muted border-[#E8E2D9]"}`}>Geen</button>
                  {[1, 2, 3, 4, 5].map((v) => (
                    <button key={v} type="button" data-testid={`block-load-${v}`} onClick={() => { setBlockModal({ ...blockModal, load: v }); setBlockWarn([]); }}
                      className={`w-9 h-9 rounded-lg border text-sm transition-colors ${Number(blockModal.load) === v ? "bg-brand text-white border-brand" : "bg-white text-ink-muted border-[#E8E2D9]"}`}>{v}</button>
                  ))}
                </div>
                <p className="text-xs text-ink-muted mt-1">Geen = puur tijdblok zonder belasting. Kies 1-5 als dit blok (bijv. administratie) wél moet meetellen in de dagbelasting.</p>
              </div>
              {blockWarn.length > 0 && (
                <div data-testid="block-load-warning" className="rounded-xl bg-[#FBF0E6] border border-[#EAD6BE] p-3 text-sm">
                  <div className="flex items-center gap-1.5 font-medium text-[#9A6B2F] mb-1"><AlertTriangle className="w-4 h-4" /> Let op — boven maximale belasting</div>
                  <ul className="list-disc pl-5 space-y-0.5 text-[#7A5A2E]">{blockWarn.map((w, i) => <li key={i}>{w}</li>)}</ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => { setBlockModal(null); setBlockWarn([]); }}>Annuleren</Button>
            <Button onClick={() => saveBlock(blockWarn.length > 0)} data-testid="save-block-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white">{blockWarn.length > 0 ? "Toch opslaan" : (blockModal?.id ? "Opslaan" : "Blokkeren")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Slot action chooser */}
      <Dialog open={!!slotAction} onOpenChange={(o) => !o && setSlotAction(null)}>
        <DialogContent className="rounded-2xl max-w-xs" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display">Wat wil je doen?</DialogTitle></DialogHeader>
          {slotAction && (
            <p className="text-sm text-ink-muted -mt-1 capitalize">
              {format(slotStart(slotAction.day, slotAction.slotIndex), "EEEE d MMMM · HH:mm", { locale: nl })}
            </p>
          )}
          <div className="grid gap-2 pt-2">
            <Button onClick={startApptFromSlot} data-testid="slot-make-appt" className="rounded-full bg-brand hover:bg-brand-hover text-white h-12 justify-start px-4">
              <CalendarPlus className="w-4 h-4 mr-2" /> Afspraak inplannen
            </Button>
            <Button onClick={startBlockFromSlot} variant="outline" data-testid="slot-block-time" className="rounded-full h-12 justify-start px-4 border-[#E8E2D9]">
              <Ban className="w-4 h-4 mr-2" /> Tijd blokkeren
            </Button>
            <Button onClick={startPrivateFromSlot} variant="outline" data-testid="slot-private" className="rounded-full h-12 justify-start px-4 border-[#E8E2D9]">
              <UserRound className="w-4 h-4 mr-2" /> Privé afspraak
            </Button>
            <Button onClick={() => {
                const st = slotStart(slotAction.day, slotAction.slotIndex);
                const d = format(st, "yyyy-MM-dd"); const hh = format(st, "HH:mm");
                const endM = new Date(st.getTime() + 4 * 3600000);
                navigate(`/admin/campagnes?date=${d}&start=${hh}&end=${format(endM, "HH:mm")}`);
              }} variant="outline" data-testid="slot-offer-time" className="rounded-full h-12 justify-start px-4 border-[#E8E2D9]">
              <Sparkles className="w-4 h-4 mr-2" /> Aanbieden aan klanten
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Drag-to-reschedule confirm */}
      <Dialog open={!!rescheduleDrag} onOpenChange={(o) => !o && setRescheduleDrag(null)}>
        <DialogContent className="rounded-2xl max-w-sm" aria-describedby={undefined} data-testid="reschedule-drag-dialog">
          <DialogHeader><DialogTitle className="font-display">Afspraak verzetten?</DialogTitle></DialogHeader>
          {rescheduleDrag && (
            <div className="text-sm text-ink space-y-2">
              <p><span className="text-ink-muted">Klant:</span> <strong>{rescheduleDrag.appt.customer_name}</strong></p>
              <p className="capitalize"><span className="text-ink-muted normal-case">Was:</span> {format(parseISO(rescheduleDrag.appt.start), "EEEE d MMMM · HH:mm", { locale: nl })}</p>
              <p className="capitalize"><span className="text-ink-muted normal-case">Nieuw:</span> {format(rescheduleDrag.start, "EEEE d MMMM · HH:mm", { locale: nl })}</p>
              <p className="text-ink-muted pt-1">De klant krijgt een e-mail/SMS met een link om de nieuwe tijd te bevestigen.</p>
              {rescheduleDrag.closedDay && (
                <p className="rounded-lg bg-[#FBEBD2] border border-dashed border-[#E0A24A] text-[#9A5B12] px-2.5 py-1.5 text-xs flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Let op: de nieuwe tijd valt buiten de openingstijden (gesloten dag/tijd).</p>
              )}
            </div>
          )}
          <div className="grid gap-2 pt-2">
            <Button onClick={confirmRescheduleDrag} data-testid="reschedule-drag-confirm" className="rounded-full bg-brand hover:bg-brand-hover text-white h-11">
              Verzetvoorstel sturen
            </Button>
            <Button onClick={moveApptDirect} variant="outline" data-testid="reschedule-drag-move" className="rounded-full h-11 border-[#E8E2D9]">
              Alleen verplaatsen (geen bericht)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Private appointment modal */}
      <Dialog open={!!privateModal} onOpenChange={(o) => { if (!o) { setPrivateModal(null); setPrivWarn([]); } }}>
        <DialogContent className="rounded-2xl" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><UserRound className="w-5 h-5 text-brand" /> {privateModal?.id ? "Privé afspraak bewerken" : "Privé afspraak"}</DialogTitle></DialogHeader>
          {privateModal && (
            <div className="space-y-4">
              <p className="text-sm text-ink-muted capitalize">{privateModal.dates && privateModal.dates.length > 1 ? `${privateModal.dates.length} dagen geselecteerd` : format(parseISO(`${privateModal.date}T00:00:00`), "EEEE d MMMM yyyy", { locale: nl })}</p>
              <div>
                <Label className="text-sm text-ink-muted mb-1.5 block">Titel *</Label>
                <Input data-testid="private-title" value={privateModal.title} onChange={(e) => setPrivateModal({ ...privateModal, title: e.target.value })} placeholder="Bijv. tandarts, lunch, privé" className="rounded-xl border-[#E8E2D9]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-sm text-ink-muted mb-1.5 block">Van</Label><Input type="time" step="900" data-testid="private-start" value={privateModal.start} onChange={(e) => { setPrivateModal({ ...privateModal, start: e.target.value }); setPrivWarn([]); }} className="rounded-xl border-[#E8E2D9]" /></div>
                <div><Label className="text-sm text-ink-muted mb-1.5 block">Tot</Label><Input type="time" step="900" data-testid="private-end" value={privateModal.end} onChange={(e) => { setPrivateModal({ ...privateModal, end: e.target.value }); setPrivWarn([]); }} className="rounded-xl border-[#E8E2D9]" /></div>
              </div>
              <div>
                <Label className="text-sm text-ink-muted mb-1.5 block">Kleur</Label>
                <div className="flex gap-2">
                  {TODO_COLORS.map((c) => (
                    <button key={c.value} type="button" onClick={() => setPrivateModal({ ...privateModal, color: c.value })} title={c.label}
                      data-testid={`private-color-${c.value.replace('#', '')}`}
                      className={`w-7 h-7 rounded-full border-2 transition-transform ${privateModal.color === c.value ? "scale-110 border-ink" : "border-white"}`}
                      style={{ backgroundColor: c.value }} />
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-sm text-ink-muted mb-1.5 block">Belasting (optioneel)</Label>
                <div className="flex gap-2 flex-wrap">
                  <button type="button" data-testid="private-load-auto" onClick={() => setPrivateModal({ ...privateModal, load: 0 })}
                    className={`h-9 px-3 rounded-lg border text-sm transition-colors ${!privateModal.load ? "bg-brand text-white border-brand" : "bg-white text-ink-muted border-[#E8E2D9]"}`}>Auto</button>
                  {[1, 2, 3, 4, 5].map((v) => (
                    <button key={v} type="button" data-testid={`private-load-${v}`} onClick={() => setPrivateModal({ ...privateModal, load: v })}
                      className={`w-9 h-9 rounded-lg border text-sm transition-colors ${Number(privateModal.load) === v ? "bg-brand text-white border-brand" : "bg-white text-ink-muted border-[#E8E2D9]"}`}>{v}</button>
                  ))}
                </div>
                <p className="text-xs text-ink-muted mt-1">Auto = afleiden uit de duur ({loadFromDur(toMin(privateModal.end) - toMin(privateModal.start)) || 1}). Kies 1-5 om te overschrijven.</p>
              </div>
              <div className="rounded-xl bg-[#FBEBD2] border border-dashed border-[#E0A24A] text-[#9A5B12] px-3 py-2 text-xs">Dit tijdvak wordt ook geblokkeerd voor online reserveringen.</div>
              {privWarn.length > 0 && (
                <div data-testid="private-load-warning" className="rounded-xl bg-[#FBF0E6] border border-[#EAD6BE] p-3 text-sm">
                  <div className="flex items-center gap-1.5 font-medium text-[#9A6B2F] mb-1"><AlertTriangle className="w-4 h-4" /> Let op — boven maximale belasting</div>
                  <ul className="list-disc pl-5 space-y-0.5 text-[#7A5A2E]">{privWarn.map((w, i) => <li key={i}>{w}</li>)}</ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => { setPrivateModal(null); setPrivWarn([]); }}>Annuleren</Button>
            <Button onClick={() => savePrivate(privWarn.length > 0)} data-testid="save-private-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white">{privWarn.length > 0 ? "Toch opslaan" : (privateModal?.id ? "Opslaan" : "Toevoegen")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* To-do subtaken afvinken vanuit agenda */}
      <Dialog open={!!todoDetail} onOpenChange={(o) => !o && setTodoDetail(null)}>
        <DialogContent className="rounded-2xl" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><CheckSquare className="w-5 h-5 text-brand" /> {todoDetail?.title}</DialogTitle></DialogHeader>
          {todoDetail && (
            <div className="space-y-4">
              {todoDetail.notes && <p className="text-sm text-ink-muted">{todoDetail.notes}</p>}
              {(todoDetail.subtasks || []).length > 0 ? (
                <div className="space-y-1" data-testid="agenda-todo-subtasks">
                  {todoDetail.subtasks.map((st) => (
                    <button key={st.id} onClick={() => toggleDetailSub(st.id)} data-testid={`agenda-subtask-${st.id}`}
                      className="flex items-center gap-2 text-left w-full py-1 group/sub">
                      {st.done ? <CheckCircle2 className="w-5 h-5 text-brand shrink-0" /> : <Circle className="w-5 h-5 text-ink-muted group-hover/sub:text-brand shrink-0" />}
                      <span className={`text-sm ${st.done ? "line-through text-ink-muted" : ""}`}>{st.text}</span>
                    </button>
                  ))}
                  <p className="text-xs text-ink-muted pt-1">{todoDetail.subtasks.filter((s) => s.done).length}/{todoDetail.subtasks.length} stappen afgerond</p>
                </div>
              ) : <p className="text-sm text-ink-muted">Deze to-do heeft geen subtaken.</p>}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => navigate("/admin/todos")}>Openen in To-Do's</Button>
            <Button onClick={finishTodo} data-testid="agenda-todo-done-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white">To-do afronden</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1 border-b border-[#F2EBE1] last:border-0">
    <span className="text-ink-muted">{label}</span><span className="text-right font-medium capitalize">{value}</span>
  </div>
);

function CustomerPicker({ customers, value, onChange, onCreated }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [nc, setNc] = useState({ name: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const selected = customers.find((c) => c.id === value);

  const startCreate = () => { setNc({ name: query, email: "", phone: "" }); setCreating(true); };

  const create = async () => {
    if (!nc.name || !nc.email || !nc.phone) { toast.error("Naam, e-mail en telefoon zijn verplicht."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/customers", nc);
      toast.success("Klant aangemaakt.");
      await onCreated();
      onChange(data.id);
      setCreating(false); setOpen(false); setQuery(""); setNc({ name: "", email: "", phone: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setCreating(false); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" data-testid="assign-customer"
          className="w-full justify-between rounded-xl border-[#E8E2D9] font-normal">
          <span className="truncate">{selected ? `${selected.name} · ${selected.phone}` : "Kies of zoek een klant"}</span>
          <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        {!creating ? (
          <Command>
            <CommandInput placeholder="Zoek op naam, telefoon of e-mail..." value={query} onValueChange={setQuery} data-testid="assign-customer-search" />
            <CommandList>
              <CommandEmpty>
                <button onClick={startCreate} data-testid="assign-new-customer-empty"
                  className="flex items-center gap-2 text-sm text-brand hover:underline px-3 py-1 mx-auto">
                  <Plus className="w-4 h-4" /> Nieuwe klant "{query}" aanmaken
                </button>
              </CommandEmpty>
              <CommandGroup>
                {customers.map((c) => (
                  <CommandItem key={c.id} value={`${c.name} ${c.phone} ${c.email}`}
                    onSelect={() => { onChange(c.id); setOpen(false); }} data-testid={`assign-customer-option-${c.id}`}>
                    <Check className={`w-4 h-4 mr-2 ${value === c.id ? "opacity-100" : "opacity-0"}`} />
                    <span>{c.name}</span><span className="text-ink-muted ml-2 text-xs">{c.phone}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
              <div className="border-t border-[#F2EBE1] p-1">
                <button onClick={startCreate} data-testid="assign-new-customer-btn"
                  className="w-full flex items-center gap-2 text-sm text-brand hover:bg-brand-light/40 rounded-md px-2 py-2">
                  <Plus className="w-4 h-4" /> Nieuwe klant aanmaken
                </button>
              </div>
            </CommandList>
          </Command>
        ) : (
          <div className="p-3 space-y-2">
            <div className="text-sm font-medium mb-1">Nieuwe klant</div>
            <Input placeholder="Naam" value={nc.name} onChange={(e) => setNc({ ...nc, name: e.target.value })} className="rounded-lg border-[#E8E2D9]" data-testid="new-cust-name" />
            <Input placeholder="E-mail" type="email" value={nc.email} onChange={(e) => setNc({ ...nc, email: e.target.value })} className="rounded-lg border-[#E8E2D9]" data-testid="new-cust-email" />
            <Input placeholder="Telefoon" value={nc.phone} onChange={(e) => setNc({ ...nc, phone: e.target.value })} className="rounded-lg border-[#E8E2D9]" data-testid="new-cust-phone" />
            <div className="flex gap-2 pt-1">
              <Button variant="outline" onClick={() => setCreating(false)} className="rounded-full flex-1">Terug</Button>
              <Button onClick={create} disabled={saving} data-testid="new-cust-save" className="rounded-full flex-1 bg-brand hover:bg-brand-hover text-white">{saving ? "..." : "Aanmaken"}</Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}


function DayEditDialog({ date, meta, onClose, onSaved }) {
  const ds = format(date, "yyyy-MM-dd");
  const [summary, setSummary] = useState(meta?.summary || "");
  const [load, setLoad] = useState(meta?.manual_load || 0);
  const [feltMental, setFeltMental] = useState(meta?.felt_mental || 0);
  const [feltWorkload, setFeltWorkload] = useState(meta?.felt_workload || 0);
  const [saving, setSaving] = useState(false);
  const [maxLoad, setMaxLoad] = useState(8);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      const wd = (date.getDay() + 6) % 7;
      const arr = r.data?.daily_max_load_by_day || [];
      setMaxLoad((arr[wd] && arr[wd] > 0) ? arr[wd] : (r.data?.daily_max_load || 8));
    }).catch(() => {});
  }, [date]);

  const toggleRec = async () => {
    if (recording) { recRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (!blob.size) return;
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "audio.webm");
          const r = await api.post("/admin/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          const txt = (r.data?.text || "").trim();
          if (txt) setSummary((prev) => prev ? `${prev} ${txt}` : txt);
          else toast.error("Geen spraak herkend.");
        } catch (e) { toast.error("Transcriptie mislukt."); }
        finally { setTranscribing(false); }
      };
      recRef.current = mr; mr.start(); setRecording(true);
    } catch (e) { toast.error("Geen toegang tot microfoon."); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/day-meta/${ds}`, { summary, manual_load: Number(load) || 0, felt_mental: feltMental, felt_workload: feltWorkload });
      toast.success("Dag opgeslagen.");
      onSaved();
    } catch (e) { toast.error("Kon dag niet opslaan."); }
    finally { setSaving(false); }
  };

  const scale = (value, onChange, testid, labels) => (
    <div className="flex gap-1.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(value === n ? 0 : n)} title={labels?.[n]}
          className={`w-9 h-9 rounded-lg text-sm border transition-colors duration-200 ${value === n ? "bg-brand text-white border-brand" : "bg-white text-ink border-[#E8E2D9] hover:border-brand"}`}>{n}</button>
      ))}
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-md max-h-[85vh] overflow-y-auto no-scrollbar" data-testid="day-edit-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display capitalize flex items-center gap-2"><StickyNote className="w-5 h-5 text-brand" /> {format(date, "EEEE d MMMM yyyy", { locale: nl })}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-sm text-ink-muted">Dagsamenvatting</Label>
              <button type="button" onClick={toggleRec} disabled={transcribing} data-testid="day-summary-mic"
                className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${recording ? "bg-[#C2607A] text-white border-[#C2607A]" : "bg-white text-brand border-[#E8E2D9] hover:border-brand"}`}>
                {transcribing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Verwerken…</> : recording ? <><Square className="w-3 h-3 fill-current" /> Stop</> : <><Mic className="w-3.5 h-3.5" /> Inspreken</>}
              </button>
            </div>
            <Textarea data-testid="day-summary-input" value={summary} onChange={(e) => setSummary(e.target.value)} rows={4}
              placeholder="Bijv. drukke dag, veel walk-ins, extra schoonmaak… of gebruik 'Inspreken'." className="rounded-xl border-[#E8E2D9]" />
          </div>
          <div>
            <Label className="text-sm text-ink-muted mb-1.5 block">Ervaren werkbelasting <span className="text-ink-muted/70">(1 = licht, 5 = zwaar)</span></Label>
            {scale(feltWorkload, setFeltWorkload, "felt-workload-scale", { 1: "Heel licht", 2: "Licht", 3: "Gemiddeld", 4: "Zwaar", 5: "Heel zwaar" })}
          </div>
          <div>
            <Label className="text-sm text-ink-muted mb-1.5 block">Ervaren mentale belasting <span className="text-ink-muted/70">(1 = licht, 5 = zwaar)</span></Label>
            {scale(feltMental, setFeltMental, "felt-mental-scale", { 1: "Heel licht", 2: "Licht", 3: "Gemiddeld", 4: "Zwaar", 5: "Heel zwaar" })}
          </div>
          <div>
            <Label className="text-sm text-ink-muted mb-1.5 block">Handmatige belasting (extra, 0–{maxLoad})</Label>
            <div className="flex gap-1.5 flex-wrap" data-testid="day-manual-load-scale">
              {Array.from({ length: maxLoad + 1 }, (_, n) => n).map((n) => (
                <button key={n} type="button" onClick={() => setLoad(n)} data-testid={`day-load-${n}`}
                  className={`w-9 h-9 rounded-lg text-sm border transition-colors duration-200 ${load === n ? "bg-brand text-white border-brand" : "bg-white text-ink border-[#E8E2D9] hover:border-brand"}`}>{n}</button>
              ))}
            </div>
            <p className="text-xs text-ink-muted mt-1.5">Telt bovenop de afspraken van deze dag (belastingbalk, historie & autoblock).</p>
          </div>
        </div>
        <DialogFooter className="mt-2">
          <Button variant="outline" className="rounded-full" onClick={onClose}>Annuleren</Button>
          <Button onClick={save} disabled={saving} data-testid="day-edit-save" className="rounded-full bg-brand hover:bg-brand-hover text-white">{saving ? "..." : "Opslaan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
